import { Fragment, createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { logoutAndClearSite } from "../services/logout";
import { auth, db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  addDoc,
  deleteDoc,
  getDoc,
  setDoc,
  doc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { fetchCoordinators } from "../services/coordinatorService";
import {
  getConsentsPath,
  getDuplicatesPath,
  getCoordinatorEnrollmentsPath,
  getEnrollmentPeriodPath,
  getWeeklyPeriodPath,
  getCoordinatorWeeklyPath,
  getMonthlyEnrollmentGoalsPath,
  getCoordinatorScreeningsPath,
  getCoordinatorScreeningsWeeklyPath,
  getScreeningPeriodPath,
  getScreeningWeeklyPeriodPath,
  getMonthlyScreeningGoalsPath,
  getCollectionPath,
  getStudiesPath,
} from "../services/firestorePaths";
import {
  PERIOD_TYPES,
  currentPeriodKey,
  periodLabel,
  listPeriodOptions,
  isCurrentPeriod,
} from "../services/enrollmentPeriods";
import { activeSiteUsesLegacySheet } from "../services/activeTenant";
import { PFT_IMPORT_ENABLED, PFT_SURVEY_TEXT_ENABLED } from "../config/featureFlags";
import { UI_REVAMP_ENABLED } from "../config/featureFlags";
import SidebarThemeToggle from "./SidebarThemeToggle";
import { Home as HomeNavIcon } from "lucide-react";
import { patientInitials } from "../services/patientInitials";
import { surveyAnsweredOnCard } from "../services/patientSurvey";
import { loadRespondedSurveyKeys } from "../services/patientSurveyService";
import { fetchSheetPatients } from "../services/sheetPatientsService";
import { findPotentialDuplicates, sameMrnNumber } from "../services/patientDuplicateMatch";
import { DIAGNOSIS_OPTIONS, diagnosisLabel, statusLabel } from "../constants/diseaseOptions";
import PotentialDuplicateDialog from "./PotentialDuplicateDialog";
import {
  splitCurrentAndPast,
  hasUnverifiedScore,
  diagnosticsBadgeFor,
  AUTO_MOVE_SCORE,
} from "../services/enrollmentRules";
import { useTenant } from "../context/TenantContext";
import {
  goalFromDoc,
  goalPct as computeGoalPct,
  mergeLeaderboard,
  rankLeaderboard,
  daysLeftInfo,
  buildGoalHistory,
} from "../services/goalProgress";
import { GoalCircle, LeaderboardList, GoalHistoryModal } from "./coordinator/GoalWidgets";
import GoalRulesEditor from "./coordinator/GoalRulesEditor";
import { rulesForMonth, validateRule, normalizeRules, ruleText } from "../services/goalRules";
import { loadRuleSets, saveRuleSet } from "../services/goalRuleService";
import { writeMonthClose } from "../services/goalMonthClose";
import MonthClosePrompt from "./goals/MonthClosePrompt";
import {
  CRC_BOX_STATUSES,
  COORDINATOR_BOX_STAGES as SHARED_COORDINATOR_BOX_STAGES,
  isPotentialDuplicateStatus,
} from "../constants/patientBoxes";
import { motion, AnimatePresence } from "framer-motion";
import {
  cardEntrance,
  AnimatedNumber,
  modalBackdrop,
  modalPanel,
} from "./motionPresets";
import {
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  Menu,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Plus,
  LogOut,
  Search,
  X,
  Users,
  UserPlus,
  ClipboardList,
  FileText,
  Trophy,
  BarChart3,
  Activity,
  ShieldCheck,
  Layers,
  Workflow,
  HeartPulse,
  AlertTriangle,
  Clock,
  Sparkles,
  Target,
  Calendar,
  IdCard,
  Stethoscope,
  Hourglass,
  Phone,
  MessageSquare,
  BookOpen,
  Database,
  Trash2,
  Flame,
  CalendarRange,
  History,
} from "lucide-react";

// Stored values; "IPF" is shown as "ILD" via diagnosisLabel (see diseaseOptions.js).
const DIAGNOSES = DIAGNOSIS_OPTIONS;

const formatDiagnosis = (dx) =>
  Array.isArray(dx) ? dx.join(", ") : dx || "-";

const normalizeDiagnosis = (dx) =>
  Array.isArray(dx) ? dx : dx ? [dx] : [];

// The site's studies (DISEASE, STATUS) by id, so the DIAGNOSTICS badge is
// derived from each patient's recorded studies (diagnosticsBadgeFor) rather
// than the stored enrolledInDiagnostics flag. Provided once by the page.
const StudiesByIdContext = createContext(new Map());

// Display-only: prefer a saved age value, otherwise derive from DOB.
const getDisplayAge = (patient) => {
  if (patient.ageNA) return null;
  if (patient.age !== undefined && patient.age !== null && patient.age !== "") {
    return patient.age;
  }
  if (!patient.dob) return null;
  const birth = new Date(patient.dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

/* -------- Shared visuals -------- */
const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
    <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
    <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
  </div>
);

const STATUS_TONE = {
  pending: {
    chip: "bg-rose-400/15 text-rose-200 border-rose-400/30",
    dot: "bg-rose-400 shadow-[0_0_10px_2px_rgba(244,63,94,0.6)]",
  },
  "unable to reach": {
    chip: "bg-rose-400/15 text-rose-200 border-rose-400/30",
    dot: "bg-rose-400 shadow-[0_0_10px_2px_rgba(244,63,94,0.6)]",
  },
  // Yellow, just above Patient Scheduled for Screen (2026-10-26).
  "ready to schedule": {
    chip: "bg-yellow-400/15 text-yellow-200 border-yellow-400/30",
    dot: "bg-yellow-300 shadow-[0_0_10px_2px_rgba(253,224,71,0.65)]",
  },
  "patient scheduled for screen": {
    chip: "bg-amber-400/15 text-amber-200 border-amber-400/30",
    dot: "bg-amber-400 shadow-[0_0_10px_2px_rgba(251,191,36,0.6)]",
  },
  "screen failed": {
    chip: "bg-orange-400/15 text-orange-200 border-orange-400/30",
    dot: "bg-orange-400 shadow-[0_0_10px_2px_rgba(251,146,60,0.6)]",
  },
  enrolled: {
    chip: "bg-emerald-400/15 text-emerald-200 border-emerald-400/30",
    dot: "bg-emerald-400 shadow-[0_0_10px_2px_rgba(52,211,153,0.6)]",
  },
  "potential duplicate": {
    chip: "bg-rose-500/15 text-rose-200 border-rose-500/30",
    dot: "bg-rose-500 shadow-[0_0_10px_2px_rgba(244,63,94,0.7)]",
  },
  deceased: {
    chip: "bg-slate-400/15 text-slate-300 border-slate-400/30",
    dot: "bg-slate-400 shadow-[0_0_10px_2px_rgba(148,163,184,0.5)]",
  },
  "awaiting pi review": {
    chip: "bg-sky-400/15 text-sky-200 border-sky-400/30",
    dot: "bg-sky-400 shadow-[0_0_10px_2px_rgba(56,189,248,0.6)]",
  },
  // Same blue as Awaiting PI Review (2026-10-15).
  "pending medical records": {
    chip: "bg-sky-400/15 text-sky-200 border-sky-400/30",
    dot: "bg-sky-400 shadow-[0_0_10px_2px_rgba(56,189,248,0.6)]",
  },
  default: {
    chip: "bg-purple-400/15 text-purple-200 border-purple-400/30",
    dot: "bg-purple-400 shadow-[0_0_10px_2px_rgba(167,139,250,0.6)]",
  },
};

const toneFor = (status = "") => {
  const k = status.toLowerCase().trim();
  return STATUS_TONE[k] || STATUS_TONE.default;
};

// UI revamp: each box row is tinted in its own colour (darker while open)
// instead of a glowing dot. The colour comes from the box's status tone where
// one exists (so a box and its cards' chips agree); boxes that only had the
// default purple get their own here so neighbours are told apart. The tints
// themselves are in theme-v2.css (`.rx-box-row`, `data-box-tone`).
const BOX_TONE_BY_NAME = {
  "lung center referrals": "purple",
  rescreen: "fuchsia",
  "provider to screen": "teal",
  "provider to schedule": "teal",
  "not interested": "slate",
  "future studies": "violet",
};
const boxToneFor = (status = "") => {
  // A coordinator's box, "Pending (Ann)", takes its stage's colour.
  const key = String(status).toLowerCase().trim().replace(/\s*\([^)]*\)$/, "");
  if (BOX_TONE_BY_NAME[key]) return BOX_TONE_BY_NAME[key];
  if (key.startsWith("future studies")) return BOX_TONE_BY_NAME["future studies"]; // its sub-boxes
  return (toneFor(key).dot.match(/bg-([a-z]+)-/) || [])[1] || "purple";
};

/* Preserved getStatusColor for any consumers still expecting tailwind text classes */
const getStatusColor = (status = "") => {
  const normalized = status.toLowerCase().trim();
  if (["pending", "unable to reach"].includes(normalized)) return "text-rose-300";
  if (normalized === "ready to schedule") return "text-yellow-300";
  if (normalized === "patient scheduled for screen") return "text-amber-300";
  if (normalized === "screen failed") return "text-orange-300";
  if (normalized === "enrolled") return "text-emerald-300";
  if (normalized === "potential duplicate") return "text-rose-400 font-semibold";
  if (normalized === "deceased") return "text-slate-300 font-medium";
  return "text-slate-400";
};

/* -------- Box order (UI revamp) --------
   Display order only (CRC_BOX_STATUSES itself is unchanged, other features use
   it): Unable to Reach sits right above Not Interested, Deceased goes to the
   very bottom, and Future Studies keeps its place after Rescreen (where it sat
   before, right after Deceased). Any box not named here keeps its order. */
const FUTURE_STUDIES_SLOT = "__future_studies__";
const revampBoxOrder = (statuses) => {
  const moved = new Set(["Unable to Reach", "Not Interested", "Deceased"]);
  const out = [];
  statuses.forEach((s) => {
    if (moved.has(s)) return;
    out.push(s);
    if (s === "Rescreen") out.push(FUTURE_STUDIES_SLOT);
  });
  if (!out.includes(FUTURE_STUDIES_SLOT)) out.push(FUTURE_STUDIES_SLOT);
  ["Unable to Reach", "Not Interested", "Deceased"].forEach((s) => {
    if (statuses.includes(s)) out.push(s);
  });
  return out;
};

/* -------- Box header (UI revamp) --------
   A box row tinted in its colour (darker while open), the name, then the
   number of patients on the far right and the chevron. `count` left out =
   no number (Future Studies). */
const RevampBoxHeader = ({ title, tone, count, open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={!!open}
    data-box-tone={tone}
    className={`rx-box-row ${open ? "rx-box-row--open" : ""} w-full flex items-center gap-3 px-3.5 py-2.5 text-left`}
  >
    <h4 className="rx-box-title text-sm font-semibold text-white truncate flex-1 min-w-0">{title}</h4>
    {count != null && <span className="rx-box-count text-sm tabular-nums">{count}</span>}
    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
  </button>
);

/* -------- KPI tile -------- */
const KpiTile = ({
  label,
  value,
  icon,
  accent,
  valueClass = "text-white",
  suffix = "",
  index = 0,
}) => (
  <motion.div
    {...cardEntrance(index)}
    className="relative overflow-hidden rounded-2xl border border-white/10 hover:border-purple-300/30 bg-white/[0.03] backdrop-blur-xl px-3 py-2 transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-12px_rgba(139,92,246,0.4)] group"
  >
    <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-60 pointer-events-none`} />
    <div className="relative flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-[0.18em] text-slate-300/80 font-medium truncate">
          {label}
        </div>
        <div className={`text-xl font-bold tracking-tight leading-tight truncate ${valueClass}`}>
          <AnimatedNumber value={value} suffix={suffix} />
        </div>
      </div>
      <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center group-hover:scale-105 transition shrink-0">
        {icon}
      </div>
    </div>
  </motion.div>
);

// Matches the stale-coordinator-box reminder workflow's own definition of a
// "coordinator box" (functions/staleCoordinatorBoxReminders.js) so the card
// highlight lines up with what actually triggers the reminder email.
const COORDINATOR_BOX_STAGES = [
  "Pending",
  "Coordinator to Screen",
  "Schedule with Coordinator",
];
const COORDINATOR_STATUS_RE = /^(.*) \((.*)\)$/;
const STALE_BOX_DAYS = 3;

// Days a patient has been sitting in their current coordinator box, or null
// if they're not in one / it can't be determined. Excluded (returns null)
// while onHold is checked, same as the reminder email.
const daysInCoordinatorBox = (patient) => {
  if (patient.onHold) return null;
  const m = String(patient.status || "").match(COORDINATOR_STATUS_RE);
  if (!m || !COORDINATOR_BOX_STAGES.includes(m[1].trim())) return null;
  if (!patient.movedToCoordinatorAt) return null;
  const movedMs = new Date(patient.movedToCoordinatorAt).getTime();
  if (Number.isNaN(movedMs)) return null;
  return Math.floor((Date.now() - movedMs) / (24 * 60 * 60 * 1000));
};

/* -------- Patient card -------- */
// `surveyAnswered`: green outline (Pending Medical Records only; patientSurvey.js).
const PatientCard = ({ patient, status, getDaysUntil, onMoveDup, onOpen, index = 0, surveyAnswered = false }) => {
  const studiesById = useContext(StudiesByIdContext);
  const diagBadge = diagnosticsBadgeFor(patient, studiesById);
  const tone = toneFor(patient.status || status);
  const rescreenDays =
    status === "Rescreen" && patient.rescreenDate
      ? getDaysUntil(patient.rescreenDate)
      : null;
  const daysStale = daysInCoordinatorBox(patient);
  const isStaleInBox = daysStale !== null && daysStale >= STALE_BOX_DAYS;
  const displayAge = getDisplayAge(patient);

  return (
    <motion.div
      {...cardEntrance(index)}
      id={`coord-patient-${patient.source === "sheet" ? patient.token : patient.id}`}
      onClick={() => onOpen(patient)}
      title={surveyAnswered ? "Answered the survey text" : undefined}
      className={`group relative cursor-pointer rounded-2xl border backdrop-blur-xl overflow-hidden hover:border-purple-300/30 hover:shadow-[0_14px_40px_-14px_rgba(139,92,246,0.5)] hover:-translate-y-0.5 transition-all scroll-mt-24 ${
        isStaleInBox
          ? "border-orange-400/50 bg-orange-500/[0.06] shadow-[0_0_24px_-10px_rgba(251,146,60,0.5)]"
          : surveyAnswered
          ? "crc-card-survey-answered border-2 bg-white/[0.035]"
          : "border-white/10 bg-white/[0.035]"
      }`}
    >
      {patient.urgent && (
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-rose-400 via-orange-400 to-rose-400 opacity-90 pointer-events-none" />
      )}
      {!patient.urgent && patient.onHold && (
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-sky-400 via-cyan-400 to-sky-400 opacity-90 pointer-events-none" />
      )}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/[0.05] via-transparent to-blue-500/[0.05] pointer-events-none" />
      <div className="relative p-3.5 flex flex-col gap-2.5">
        {/* Top row: status chip + urgency badges */}
        <div className="flex items-start justify-between gap-1.5">
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border backdrop-blur-md ${tone.chip}`}
          >
            {!UI_REVAMP_ENABLED && <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />}
            <span className="truncate max-w-[160px]">{statusLabel(patient.status)}</span>
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {diagBadge.show && UI_REVAMP_ENABLED && (
              // Plain green text, no box (UI revamp). Same tooltip.
              <span
                className="rx-diag-tag text-[10px] font-semibold uppercase tracking-wider"
                title={
                  diagBadge.legacy
                    ? "Enrolled in a diagnostics study (no study recorded) — still eligible for other studies"
                    : `Enrolled in ${diagBadge.studies
                        .map((e) => e.studyName || e.studyId)
                        .join(", ")} — still eligible for other studies`
                }
              >
                Diagnostics
              </span>
            )}
            {diagBadge.show && !UI_REVAMP_ENABLED && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/25 border border-emerald-400/50 text-[10px] font-bold text-emerald-100 shadow-[0_0_12px_-2px_rgba(52,211,153,0.6)]"
                title={
                  diagBadge.legacy
                    ? "Enrolled in a diagnostics study (no study recorded) — still eligible for other studies"
                    : `Enrolled in ${diagBadge.studies
                        .map((e) => e.studyName || e.studyId)
                        .join(", ")} — still eligible for other studies`
                }
              >
                <Stethoscope className="w-3 h-3" />
                ENROLLED · DIAGNOSTICS
              </span>
            )}
            {patient.urgent && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-rose-500/25 border border-rose-400/50 text-[10px] font-bold text-rose-100 shadow-[0_0_12px_-2px_rgba(244,63,94,0.6)]">
                <Flame className="w-3 h-3" />
                URGENT
              </span>
            )}
            {patient.onHold && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-sky-500/25 border border-sky-400/50 text-[10px] font-bold text-sky-100 shadow-[0_0_12px_-2px_rgba(56,189,248,0.6)]">
                <Hourglass className="w-3 h-3" />
                HOLD
              </span>
            )}
            {isStaleInBox && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-orange-500/25 border border-orange-400/50 text-[10px] font-bold text-orange-100 shadow-[0_0_12px_-2px_rgba(251,146,60,0.6)]">
                <Clock className="w-3 h-3" />
                In box {daysStale}d
              </span>
            )}
            {rescreenDays !== null && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                  rescreenDays < 0
                    ? "bg-rose-500/20 border-rose-400/40 text-rose-200"
                    : rescreenDays === 0
                    ? "bg-orange-500/20 border-orange-400/40 text-orange-200"
                    : "bg-amber-500/15 border-amber-400/30 text-amber-200"
                }`}
              >
                <Clock className="w-3 h-3" />
                {rescreenDays < 0
                  ? `Overdue ${Math.abs(rescreenDays)}d`
                  : rescreenDays === 0
                  ? "Rescreen Today"
                  : `Rescreen ${rescreenDays}d`}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-sm font-bold text-white ring-1 ring-white/20 shrink-0">
            {UI_REVAMP_ENABLED
              ? patientInitials(patient.name || patient.initials)
              : (patient.name || patient.initials || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            {!UI_REVAMP_ENABLED && (
              <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold leading-none">
                Patient
              </div>
            )}
            <div className="text-sm font-semibold text-white truncate leading-tight">
              {patient.name || patient.initials || "N/A"}
            </div>
            {patient.dob && (
              <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                {!UI_REVAMP_ENABLED && <Calendar className="w-3 h-3" />}
                {patient.dob}
              </div>
            )}
          </div>
          {(patient.mrn || displayAge !== null) && (
            <div className="flex flex-col items-end gap-1 shrink-0 self-start">
              {patient.mrn && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.05] border border-white/10 text-[10px] font-mono text-purple-200">
                  {!UI_REVAMP_ENABLED && <IdCard className="w-3 h-3 text-purple-300" />}
                  {patient.mrn}
                </span>
              )}
              {displayAge !== null && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.05] border border-white/10 text-[10px] font-medium text-blue-200">
                  Age {displayAge}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Diagnosis */}
        {patient.diagnosis && (
          <div className="flex flex-wrap gap-1">
            {normalizeDiagnosis(patient.diagnosis).map((dx) => (
              <span
                key={dx}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/15 border border-purple-300/30 text-purple-200"
              >
                {!UI_REVAMP_ENABLED && <HeartPulse className="w-2.5 h-2.5" />}
                {diagnosisLabel(dx)}
              </span>
            ))}
          </div>
        )}

        {/* Enrolled studies — with the backfill's match score until staff
            verify it, so nobody has to open each patient to check. */}
        {(() => {
          const current = splitCurrentAndPast(patient.enrolledStudies, studiesById).current;
          if (!current.length) return null;
          return (
            <div className="flex flex-wrap gap-1">
              {current.slice(0, 3).map((e) => {
                const scored = hasUnverifiedScore(e);
                const low = scored && e.matchScore < AUTO_MOVE_SCORE;
                return (
                  <span
                    key={`${e.studyId}-${e.enrolledAt || ""}`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                      low
                        ? "bg-amber-400/15 border-amber-300/40 text-amber-100"
                        : "bg-emerald-500/15 border-emerald-300/30 text-emerald-100"
                    }`}
                    title={scored ? (e.matchReasons || []).join("; ") : undefined}
                  >
                    {!UI_REVAMP_ENABLED && <Stethoscope className="w-2.5 h-2.5" />}
                    <span className="truncate max-w-[140px]">{e.studyName || e.studyId}</span>
                    {scored && <span className="font-semibold">· {e.matchScore}%{low ? " review" : ""}</span>}
                    {e.approval === "pending" && <span className="font-semibold">· pending</span>}
                  </span>
                );
              })}
              {current.length > 3 && (
                <span className="text-[10px] text-slate-400 self-center">+{current.length - 3}</span>
              )}
            </div>
          );
        })()}

        {/* Footer: last edited + action */}
        <div className="pt-2 border-t border-white/5 flex items-end justify-between gap-2">
          {patient.lastEditedAt ? (
            <div className="text-[10px] text-slate-500 leading-tight min-w-0">
              <div className="truncate">
                Saved by {patient.lastEditedBy?.split("@")[0] || "—"}
              </div>
              <div className="truncate">
                {patient.lastEditedAt?.seconds
                  ? new Date(patient.lastEditedAt.seconds * 1000).toLocaleString()
                  : new Date(patient.lastEditedAt).toLocaleString()}
              </div>
            </div>
          ) : (
            <span className="text-[10px] text-slate-500">—</span>
          )}
          {/* UI revamp: not on the card (user, 2026-10-30). The chart's status
              picker ("Potential Duplicate") and Scan for Duplicates still move. */}
          {onMoveDup && !UI_REVAMP_ENABLED && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMoveDup(patient);
              }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 hover:bg-rose-500/15 hover:border-rose-400/30 text-[10px] font-medium text-slate-300 hover:text-rose-200 transition shrink-0"
              title="Move to Duplicates"
            >
              <Trash2 className="w-3 h-3" />
              Move to Duplicates
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const CoordinatorPage = () => {
  const [patients, setPatients] = useState([]);
  // For the DIAGNOSTICS badge (see StudiesByIdContext). Until it loads, cards
  // fall back to each entry's disease snapshot, so nothing flickers wrongly.
  const [studiesById, setStudiesById] = useState(() => new Map());
  useEffect(() => {
    let cancelled = false;
    getDocs(collection(db, getStudiesPath()))
      .then((snap) => {
        if (!cancelled) setStudiesById(new Map(snap.docs.map((d) => [d.id, { id: d.id, ...d.data() }])));
      })
      .catch((err) => console.error("Couldn't load studies for badges:", err));
    return () => {
      cancelled = true;
    };
  }, []);
  const [duplicates, setDuplicates] = useState([]);
  // Status doc ids of patients who answered the survey text (patientSurvey.js).
  const [respondedSurveyKeys, setRespondedSurveyKeys] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  // True when the legacy sheet could not be read. Kept separate from `loading`
  // and from an empty list on purpose: "the sheet is down" and "this patient is
  // not in the system" must never look the same to a coordinator.
  const [sheetUnavailable, setSheetUnavailable] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [searchQuery, setSearchQuery] = useState({});
  const [masterSearch, setMasterSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("statuses");
  const [coordinatorExpanded, setCoordinatorExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");

  const [newPatient, setNewPatient] = useState({
    name: "",
    dob: "",
    mrn: "",
    diagnosis: [],
  });

  // Name + DOB duplicate warning. Holds the matches found for the patient
  // currently being added; non-empty means the confirmation dialog is up.
  // Advisory only. The exact-MRN check above it still blocks outright.
  const [dupMatches, setDupMatches] = useState([]);

  const [coordinatorCounts, setCoordinatorCounts] = useState([]);
  const [weeklyCounts, setWeeklyCounts] = useState([]);
  // Which month / ISO week the leaderboards are showing.
  //
  // Counts are stored per period, so a manager back from leave on 1 September
  // can select August and enter it without touching September. Defaults to the
  // current period, so the ordinary case is unchanged.
  const [monthId, setMonthId] = useState(() => currentPeriodKey(PERIOD_TYPES.MONTH));
  const [weekId, setWeekId] = useState(() => currentPeriodKey(PERIOD_TYPES.WEEK));
  const [monthsWithData, setMonthsWithData] = useState([]);
  const [weeksWithData, setWeeksWithData] = useState([]);
  // Periods that have been formally closed — a snapshot was taken and signed.
  const [closedMonths, setClosedMonths] = useState({});
  // What the dropdowns offer: the current period, a few recent ones even when
  // empty (so a month nobody opened can still be filled in), plus anything with
  // data however old.
  // Periods that hold SCREENING counts. Kept apart from the enrollment lists so
  // a failed screening read can never hide an enrollment month.
  const [screeningMonthsWithData, setScreeningMonthsWithData] = useState([]);
  const [screeningWeeksWithData, setScreeningWeeksWithData] = useState([]);
  const monthOptions = useMemo(
    () => listPeriodOptions(PERIOD_TYPES.MONTH, [...monthsWithData, ...screeningMonthsWithData]),
    [monthsWithData, screeningMonthsWithData]
  );
  const weekOptions = useMemo(
    () => listPeriodOptions(PERIOD_TYPES.WEEK, [...weeksWithData, ...screeningWeeksWithData]),
    [weeksWithData, screeningWeeksWithData]
  );
  const viewingPastMonth = !isCurrentPeriod(PERIOD_TYPES.MONTH, monthId);
  const viewingPastWeek = !isCurrentPeriod(PERIOD_TYPES.WEEK, weekId);

  // UI-only sidebar/widget toggles
  const [isCommandDockOpen, setIsCommandDockOpen] = useState(true);
  const [mobileDockOpen, setMobileDockOpen] = useState(false);
  const [leaderboardMode, setLeaderboardMode] = useState("monthly"); // monthly | weekly
  const [showLeaderboardEdit, setShowLeaderboardEdit] = useState(false);
  const [leaderboardDraft, setLeaderboardDraft] = useState({}); // { coord: { monthly, weekly } }
  const [leaderboardSaving, setLeaderboardSaving] = useState(false);
  const [savingAndResetting, setSavingAndResetting] = useState(false);
  const [showGoalEdit, setShowGoalEdit] = useState(false);
const [goalDraft, setGoalDraft] = useState("");
const [goalSaving, setGoalSaving] = useState(false);
const [enrollmentGoal, setEnrollmentGoal] = useState(12);
  // Second goal circle + leaderboard: screenings, hand-entered like enrollments.
  // Goal null = "Goal not set" (no default, unlike enrollments' 12).
  const [screeningCounts, setScreeningCounts] = useState([]);
  const [screeningWeeklyCounts, setScreeningWeeklyCounts] = useState([]);
  const [screeningGoal, setScreeningGoal] = useState(null);
  // UI revamp: which month each goal above has finished loading for, so the
  // home page's goal pencil can open Edit goal only once its numbers are in.
  const [goalLoadedFor, setGoalLoadedFor] = useState({ enrollments: "", screenings: "" });
  // Which metric the shared goal / counts pop-ups are editing.
  const [goalEditMetric, setGoalEditMetric] = useState("enrollments");
  // Phase 2 rules for the goal being edited (admins): the draft, what was
  // loaded (to know if it changed), and where they were inherited from.
  const [ruleDraft, setRuleDraft] = useState([]);
  const [ruleBaseline, setRuleBaseline] = useState("[]");
  const [rulesInheritedFrom, setRulesInheritedFrom] = useState(null);
  const [rulesLoad, setRulesLoad] = useState({ loading: false, error: "" });
  const rulesDirty = JSON.stringify(normalizeRules(ruleDraft)) !== ruleBaseline;
  const [leaderboardEditMetric, setLeaderboardEditMetric] = useState("enrollments");
  const [showGoalHistory, setShowGoalHistory] = useState(false);
  const [goalHistory, setGoalHistory] = useState({ loading: false, error: "", rows: [] });
  // Admin-only controls here are gated on the user's role FOR THE ACTIVE SITE,
  // NOT the legacy flat user_roles.role. Someone who is "admin" at the org root
  // but only a coordinator on this site must not see admin UI/actions (e.g. the
  // "Back to Admin Dashboard" button). Owners and tenant admins still qualify.
  const { isOwner, isTenantAdmin, roleForActiveSite, tenantId: activeTenantId } =
    useTenant();
  const isAdmin =
    isOwner || isTenantAdmin(activeTenantId) || roleForActiveSite === "admin";
  // PFT Uploads is for admins and coordinators (the route enforces the same).
  const canSeePftUploads = isAdmin || roleForActiveSite === "coordinator";
  // Scan for Duplicates: same roles as its route (reception can't open patient pages).
  const canScanDuplicates = isAdmin || roleForActiveSite === "coordinator";
  const navigate = useNavigate();
  const location = useLocation();
  // UI revamp: the home sidebar's "Add Patient" opens this page with the Add
  // Patient pop-up open; the hand-off is used once (a refresh doesn't reopen it).
  useEffect(() => {
    if (UI_REVAMP_ENABLED && location.state?.addPatient) {
      setShowModal(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // UI revamp: the home page's goal pencil opens this page's own Edit goal
  // pop-up for that month (the one place goals and rules are edited), once
  // that month's goals have loaded. Used once; a refresh doesn't reopen it.
  const [pendingGoalEdit, setPendingGoalEdit] = useState(null);
  useEffect(() => {
    const g = UI_REVAMP_ENABLED ? location.state?.goalEdit : null;
    if (!g || !["enrollments", "screenings"].includes(g.metric)) return;
    if (/^\d{4}-\d{2}$/.test(String(g.monthId || ""))) setMonthId(g.monthId);
    setPendingGoalEdit(g);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Patient to scroll back to after returning from PatientDetailPage.
  const returnPatientIdRef = useRef(null);

  // After data + the right section render, scroll the returned-to card into
  // view and briefly highlight it, so users land exactly where they left off.
  useEffect(() => {
    if (loading) return;
    const pid = returnPatientIdRef.current;
    if (!pid) return;
    const el = document.getElementById(`coord-patient-${pid}`);
    if (!el) return; // section not rendered yet; a later render will catch it
    returnPatientIdRef.current = null;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-purple-400/70");
      setTimeout(() => el.classList.remove("ring-2", "ring-purple-400/70"), 2200);
    });
  }, [loading, patients, duplicates, activeTab, expanded, coordinatorExpanded]);

  const goToVaultX = () => navigate("/ebinder");
  const goToStatistics = () => navigate("/statistics");
  const goToSurvey = () => navigate("/weekly-survey");
  const goToAdminDashboard = () => navigate("/dashboard");
  const goToEnrolledPatients = () => navigate("/coordinator/study-patients");
  const goToStudies = () => navigate("/studies");
  const goToContacts = () => navigate("/contacts");
  const goToQuery = () => navigate("/query");
  const goToSourceX = () => navigate("/sourcex");
  const goToRawDatabase = () => navigate("/coordinator/raw-database");
  const goToPftUploads = () => navigate("/coordinator/pft-uploads");
  // A new tab, so the CRC page stays where it was while records are checked.
  const openDuplicateScan = () => window.open("/coordinator/duplicate-scan", "_blank", "noopener,noreferrer");
  const goToResearchCalendar = () => navigate("/research-calendar");


  // The boxes (src/constants/patientBoxes.js, shared with the weekly report's
  // "Add to RegX").
  const commonStatuses = CRC_BOX_STATUSES;
  // Stored diagnosis values → "Future Studies - X" boxes. Boxes are keyed and
  // filtered on the stored status string; only the header text is relabeled.
  const futureStudyDiagnoses = DIAGNOSIS_OPTIONS;

  const coordinatorStatusTypes = SHARED_COORDINATOR_BOX_STAGES;

  // Coordinators are managed in Firestore (the `coordinators` collection) via the
  // admin "Manage Coordinators" page — no longer hardcoded.
  //   - `coordinators`: active coordinator names → drive the per-coordinator
  //     status blocks (everyone, including lead coordinators like Levi).
  //   - `leaderboardCoordinators`: active AND includeInLeaderboard → drive the
  //     leaderboard merges + edit modal (this is where Levi stays excluded).
  const [coordinators, setCoordinators] = useState([]);
  const [leaderboardCoordinators, setLeaderboardCoordinators] = useState([]);
  // (isAdmin is derived from the active-site membership above — no effect needed.)
// The goal follows the SELECTED month, not the wall clock. Previously the goal
// was keyed to `new Date()` while the counts were a single running number, so
// on the 1st the screen showed this month's goal against last month's counts.
useEffect(() => {
  const fetchEnrollmentGoal = async () => {
    if (!monthId) return;
    try {
      const goalSnap = await getDoc(doc(db, getMonthlyEnrollmentGoalsPath(), monthId));
      setEnrollmentGoal(
        goalSnap.exists() ? Math.max(Number(goalSnap.data()?.goal) || 12, 0) : 12
      );
    } catch (err) {
      console.error("❌ Error loading enrollment goal:", err);
      setEnrollmentGoal(12);
    }
    setGoalLoadedFor((prev) => ({ ...prev, enrollments: monthId }));
  };

  fetchEnrollmentGoal();
}, [monthId]);

// Screening goal for the selected month. Its own effect and its own catch, so
// a screening read failure can never touch the enrollment circle.
useEffect(() => {
  if (!monthId) return;
  let cancelled = false;
  getDoc(doc(db, getMonthlyScreeningGoalsPath(), monthId))
    .then((snap) => {
      if (!cancelled) setScreeningGoal(goalFromDoc("screenings", snap.exists(), snap.data()));
    })
    .catch((err) => {
      console.error("❌ Error loading screening goal:", err);
      if (!cancelled) setScreeningGoal(null);
    })
    .finally(() => {
      if (!cancelled) setGoalLoadedFor((prev) => ({ ...prev, screenings: monthId }));
    });
  return () => {
    cancelled = true;
  };
}, [monthId]);

// Periods that hold screening counts, for the shared month/week dropdowns.
useEffect(() => {
  const isMonth = (id) => /^\d{4}-\d{2}$/.test(id);
  const isWeek = (id) => /^\d{4}-W\d{2}$/.test(id);
  Promise.all([
    getDocs(collection(db, getCoordinatorScreeningsPath())),
    getDocs(collection(db, getCoordinatorScreeningsWeeklyPath())),
  ])
    .then(([mSnap, wSnap]) => {
      setScreeningMonthsWithData(mSnap.docs.map((d) => d.id).filter(isMonth));
      setScreeningWeeksWithData(wSnap.docs.map((d) => d.id).filter(isWeek));
    })
    .catch((err) => console.error("❌ Error loading screening periods:", err));
}, []);

// Screening counts for the selected month and week, merged against the same
// leaderboard roster as enrollments (set by fetchData below).
useEffect(() => {
  if (!monthId || !weekId) return;
  let cancelled = false;
  const readPeriod = async (path) =>
    (await getDocs(collection(db, path))).docs.map((d) => ({ name: d.id, ...d.data() }));
  Promise.all([readPeriod(getScreeningPeriodPath(monthId)), readPeriod(getScreeningWeeklyPeriodPath(weekId))])
    .then(([monthDocs, weekDocs]) => {
      if (cancelled) return;
      setScreeningCounts(mergeLeaderboard(monthDocs, leaderboardCoordinators).rows);
      setScreeningWeeklyCounts(mergeLeaderboard(weekDocs, leaderboardCoordinators).rows);
    })
    .catch((err) => {
      console.error("❌ Error loading screening counts:", err);
      if (cancelled) return;
      setScreeningCounts(mergeLeaderboard([], leaderboardCoordinators).rows);
      setScreeningWeeklyCounts(mergeLeaderboard([], leaderboardCoordinators).rows);
    });
  return () => {
    cancelled = true;
  };
}, [monthId, weekId, leaderboardCoordinators]);

// Which periods already hold counts, so the dropdowns can offer them. Recent
// empty periods are offered too — that is what lets somebody fill in a month
// nobody ever opened.
useEffect(() => {
  const loadPeriods = async () => {
    try {
      const [mSnap, wSnap] = await Promise.all([
        getDocs(collection(db, getCoordinatorEnrollmentsPath())),
        getDocs(collection(db, getCoordinatorWeeklyPath())),
      ]);
      const isMonth = (id) => /^\d{4}-\d{2}$/.test(id);
      const isWeek = (id) => /^\d{4}-W\d{2}$/.test(id);
      setMonthsWithData(mSnap.docs.map((d) => d.id).filter(isMonth));
      setWeeksWithData(wSnap.docs.map((d) => d.id).filter(isWeek));
      const closed = {};
      mSnap.docs.forEach((d) => {
        const v = d.data() || {};
        if (isMonth(d.id) && v.closedAt) closed[d.id] = v;
      });
      setClosedMonths(closed);
    } catch (err) {
      console.error("❌ Error loading enrollment periods:", err);
    }
  };
  loadPeriods();
}, []);
  // Load patients + coordinator enrollments
  useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);

      // Patients from sheet (Dubois-only legacy store) + Firestore.
      // Other sites are Firestore-only, so skip the Sheet entirely.
      let sheetPatients = [];
      if (activeSiteUsesLegacySheet()) {
        try {
          // Retries transient upstream failures (the Sheets API returned 503 for
          // several minutes on 2026-08-13) before giving up.
          sheetPatients = await fetchSheetPatients();
          setSheetUnavailable(false);
        } catch (err) {
          // Caught HERE rather than by the outer handler, deliberately. This
          // fetch used to sit inside the one big try, so a sheet outage also
          // aborted the Firestore loads and the navigation-state handling below,
          // leaving the rest of the component half-initialised. Isolating it
          // lets the rest of fetchData finish cleanly; the flag then hides the
          // patient list and its counts, since a partial view is worse than none.
          console.error("❌ Legacy patient sheet unavailable:", err);
          setSheetUnavailable(true);
          sheetPatients = [];
        }
      }
      const processedSheet = sheetPatients.map((p) => ({
  ...p,

  // Google Sheet sends initials in `name`, but real patient name in `fullName`
  name: p.fullName || p.name || p.initials || "N/A",
  initials: p.initials || p.name || "",

  status: p.status?.trim() || "Lung Center Referrals",
  source: "sheet",
}));

      const consentsRef = collection(db, getConsentsPath());
      const q = query(consentsRef, orderBy("signedAt", "desc"));
      const fsSnap = await getDocs(q);
      const fsPatients = fsSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        source: "firestore",
        status: docSnap.data().status || "Lung Center Referrals",
      }));

      const dupSnap = await getDocs(collection(db, getDuplicatesPath()));
      const dupData = dupSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
        source: "duplicates",
        status: docSnap.data().status || "Potential Duplicate",
      }));

      // A Sheet patient set to "Potential Duplicate" (from the chart, or Scan for
      // Duplicates' Move) is shown like a moved RegX patient: in the Duplicates
      // tile/tab, not the patient list. Display only; the row is unchanged.
      const isSheetDup = (p) => isPotentialDuplicateStatus(p.status);
      setPatients([...processedSheet.filter((p) => !isSheetDup(p)), ...fsPatients]);
      setDuplicates([...dupData, ...processedSheet.filter(isSheetDup)]);

      // Who answered the survey text, for the green outline in Pending Medical
      // Records. Not awaited and fail-soft: it can never hold up or break the list
      // (reception can't read it; their cards just use the record's own marker).
      if (PFT_SURVEY_TEXT_ENABLED) {
        loadRespondedSurveyKeys()
          .then(setRespondedSurveyKeys)
          .catch(() => setRespondedSurveyKeys(new Set()));
      }

      // Load the managed coordinator list (replaces the old hardcoded arrays).
      const allCoordinators = await fetchCoordinators();
      const activeCoordinators = allCoordinators.filter((c) => c.active !== false);
      const activeNames = activeCoordinators.map((c) => c.name);
      const leaderboardNames = activeCoordinators
        .filter((c) => c.includeInLeaderboard !== false)
        .map((c) => c.name);
      setCoordinators(activeNames);
      setLeaderboardCoordinators(leaderboardNames);

      // Counts for the SELECTED month. A month nobody has written to reads as
      // all zeros, which is correct — a new month genuinely starts empty, and
      // that is what removed the monthly reset ritual.
      const enrollRef = collection(db, getEnrollmentPeriodPath(monthId));
      const enrollSnap = await getDocs(enrollRef);
      const enrollData = enrollSnap.docs.map((doc) => ({
        name: doc.id,
        ...doc.data(),
      }));

      const merged = leaderboardNames.map((coord) => {
        const found = enrollData.find((c) => c.name === coord);
        return { name: coord, count: found ? Number(found.count) || 0 : 0 };
      });

      setCoordinatorCounts(merged);
      // ✅ Weekly Leaderboard
const weeklyRef = collection(db, getWeeklyPeriodPath(weekId));
const weeklySnap = await getDocs(weeklyRef);
const weeklyData = weeklySnap.docs.map((doc) => ({
  name: doc.id,
  ...doc.data(),
}));

const weeklyMerged = leaderboardNames.map((coord) => {
  const found = weeklyData.find((c) => c.name === coord);
  return { name: coord, count: found ? Number(found.count) || 0 : 0 };
});

setWeeklyCounts(weeklyMerged);


      // ✅ Restore last tab + section + scroll target when coming back
      const fromTab = location.state?.fromCoordinatorTab;
      const fromSection = location.state?.fromCoordinatorSection;
      if (fromTab) setActiveTab(fromTab);
      if (fromSection) {
        // Status sections live in `expanded`; coordinator sections live in
        // `coordinatorExpanded`. Re-open the right one for the tab.
        if (fromTab === "coordinators") {
          setCoordinatorExpanded({ [fromSection]: true });
        } else {
          setExpanded({ [fromSection]: true });
        }
      }
      if (location.state?.fromCoordinatorPatientId) {
        returnPatientIdRef.current = location.state.fromCoordinatorPatientId;
      }

    } catch (err) {
      console.error("❌ Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
  // Re-reads when the selected month or week changes, so switching period
  // loads that period’s counts rather than showing the previous one’s.
}, [location.state, monthId, weekId]);
// Saves the goal of whichever circle the pop-up was opened for.
const saveGoal = async () => {
  if (!isAdmin) return;

  const nextGoal = Math.max(parseInt(goalDraft) || 0, 0);
  const isScreenings = goalEditMetric === "screenings";

  // Every rule must be complete before anything is saved.
  const badRules = ruleDraft
    .map((r, i) => ({ i, errors: validateRule(r) }))
    .filter((x) => x.errors.length);
  if (badRules.length) {
    alert(badRules.map((x) => "Rule " + (x.i + 1) + ": " + x.errors.join(" ")).join("\n"));
    return;
  }
  const savingRules = rulesDirty && !rulesLoad.error;

  setGoalSaving(true);

  try {
    // Rules first: they're the part most likely to be refused (admins only).
    if (savingRules) {
      await saveRuleSet({ metric: goalEditMetric, monthId, rules: ruleDraft, by: auth.currentUser?.email || "" });
      setRuleBaseline(JSON.stringify(normalizeRules(ruleDraft)));
      setRulesInheritedFrom(null);
    }
    // The goal belongs to the month being VIEWED, not whatever month it is
    // when somebody edits it. This local used to shadow the selected month
    // with new Date(), which is how a goal for September ended up on screen
    // beside August's counts.
    const [goalYear, goalMonth] = monthId.split("-").map(Number);

    await setDoc(
      doc(db, isScreenings ? getMonthlyScreeningGoalsPath() : getMonthlyEnrollmentGoalsPath(), monthId),
      {
        monthId,
        year: goalYear,
        month: goalMonth,
        goal: nextGoal,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || "",
      },
      { merge: true }
    );

    if (isScreenings) {
      // 0 means "no goal": the circle goes back to "Goal not set".
      setScreeningGoal(goalFromDoc("screenings", true, { goal: nextGoal }));
    } else {
      setEnrollmentGoal(nextGoal);
    }
    setShowGoalEdit(false);
    const ruleLines = ruleDraft.length
      ? ruleDraft.map((r, i) => i + 1 + ". " + ruleText(r, studiesById)).join("\n")
      : "(none)";
    alert(
      (isScreenings ? "✅ Screening goal updated." : "✅ Randomization goal updated.") +
        (savingRules
          ? "\n\nRules from " + (periodLabel(PERIOD_TYPES.MONTH, monthId) || monthId) + " on:\n" + ruleLines
          : "")
    );
  } catch (err) {
    console.error("❌ Error saving goal:", err);
    alert(isScreenings ? "Failed to save screening goal." : "Failed to save enrollment goal.");
  } finally {
    setGoalSaving(false);
  }
};
  // CLOSE the month — snapshot and sign it. It does NOT zero anything.
  //
  // The old button archived the counts and then reset every coordinator to
  // zero, because the counts were one running number with no period attached
  // and resetting was the only way to start a new month. Counts are now keyed
  // by period, so a new month begins empty on its own. Nothing has to be reset,
  // nothing can be forgotten, and clicking twice can no longer wipe a month.
  //
  // What remains is worth keeping: a deliberate point where somebody says this
  // month is done, recorded with their name and the time. A closed month stays
  // editable — a late correction is a fact of life — but it is stamped, and the
  // UI says so.
  const closeMonth = async () => {
    if (!isAdmin) return;

    const label = periodLabel(PERIOD_TYPES.MONTH, monthId) || monthId;
    const already = closedMonths[monthId];
    const ok = window.confirm(
      already
        ? `${label} was already closed. Re-closing records a new snapshot with the current numbers. Continue?`
        : `Close ${label}? This records a signed snapshot of the counts. Counts are NOT reset — the next month is already empty.`
    );
    if (!ok) return;

    setSavingAndResetting(true);

    try {
      // Same writes as the "Close last month?" pop-up (services/goalMonthClose.js):
      // a signed snapshot of both goals, stamped on the month it COVERS, and the
      // close stamp on each period document. No count is changed.
      const { total, screeningTotal } = await writeMonthClose({
        monthId,
        enrollmentRows: coordinatorCounts,
        screeningRows: screeningCounts,
        by: auth.currentUser?.email || "",
      });

      setClosedMonths((prev) => ({
        ...prev,
        [monthId]: { closedBy: auth.currentUser?.email || "", total },
      }));
      setMonthsWithData((prev) => (prev.includes(monthId) ? prev : [...prev, monthId]));

      alert(
        `✅ ${label} closed. ${total} randomization(s) and ${screeningTotal} screening(s) recorded. Counts were not changed.`
      );
    } catch (err) {
      console.error("❌ Error closing the month:", err);
      alert("Failed to close the month.");
    } finally {
      setSavingAndResetting(false);
    }
  };

  // Save updated enrollment count to Firestore
  // `metric` picks the board: "enrollments" (default, unchanged) or "screenings".
  const saveEdit = async (coord, type, valueOverride, metric = "enrollments") => {
  if (!isAdmin) return; // admin-only since 2026-10-08; firestore.rules enforces it too
  const raw = valueOverride !== undefined ? valueOverride : editValue;
  const count = Math.max(parseInt(raw) || 0, 0);
  const isMonthly = type === "quarterly"; // legacy name for the MONTHLY board
  const isScreenings = metric === "screenings";
  const periodId = isMonthly ? monthId : weekId;
  const path = isScreenings
    ? isMonthly
      ? getScreeningPeriodPath(periodId)
      : getScreeningWeeklyPeriodPath(periodId)
    : isMonthly
    ? getEnrollmentPeriodPath(periodId)
    : getWeeklyPeriodPath(periodId);

  try {
    // Stamped with who and when, because entering a past period is allowed and
    // should never be invisible — a late edit to a closed month is exactly the
    // thing a monitor asks about.
    const coordRef = doc(db, path, coord);
    // Hand edits write an absolute number. If the count changed since this page
    // loaded it (e.g. goal points approved on Admin Reports in another tab),
    // don't overwrite it: say so and show the current number instead.
    const rows = isScreenings
      ? isMonthly
        ? screeningCounts
        : screeningWeeklyCounts
      : isMonthly
      ? coordinatorCounts
      : weeklyCounts;
    const shown = Math.max(Number(rows.find((c) => c.name === coord)?.count) || 0, 0);
    const freshSnap = await getDoc(coordRef);
    const fresh = freshSnap.exists() ? Math.max(Number(freshSnap.data()?.count) || 0, 0) : 0;
    if (fresh !== shown) {
      const setFresh = (prev) => prev.map((c) => (c.name === coord ? { ...c, count: fresh } : c));
      if (isScreenings) {
        if (isMonthly) setScreeningCounts(setFresh);
        else setScreeningWeeklyCounts(setFresh);
      } else if (isMonthly) {
        setCoordinatorCounts(setFresh);
      } else {
        setWeeklyCounts(setFresh);
      }
      alert(
        `${coord}'s count changed to ${fresh} since this page loaded (for example, goal points were approved). ` +
          "It wasn't overwritten. Open Edit again to change it."
      );
      setEditing(null);
      setEditValue("");
      return;
    }
    await setDoc(
      coordRef,
      {
        count,
        periodId,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || "",
      },
      { merge: true }
    );

    // The period also needs to exist as a DOCUMENT. Writing to
    // collection/{period}/coordinators/{name} leaves the parent "missing", and
    // the client SDK cannot list missing documents — so without this a period
    // would hold data and still never appear in the dropdown.
    const parentPath = isScreenings
      ? isMonthly
        ? getCoordinatorScreeningsPath()
        : getCoordinatorScreeningsWeeklyPath()
      : isMonthly
      ? getCoordinatorEnrollmentsPath()
      : getCoordinatorWeeklyPath();
    await setDoc(
      doc(db, parentPath, periodId),
      { periodId, updatedAt: serverTimestamp() },
      { merge: true }
    );
    const addPeriod = (prev) => (prev.includes(periodId) ? prev : [...prev, periodId]);
    if (isScreenings) {
      if (isMonthly) setScreeningMonthsWithData(addPeriod);
      else setScreeningWeeksWithData(addPeriod);
    } else if (isMonthly) {
      setMonthsWithData(addPeriod);
    } else {
      setWeeksWithData(addPeriod);
    }

    const setCount = (prev) => prev.map((c) => (c.name === coord ? { ...c, count } : c));
    if (isScreenings) {
      if (isMonthly) setScreeningCounts(setCount);
      else setScreeningWeeklyCounts(setCount);
    } else if (type === "quarterly") {
      setCoordinatorCounts(setCount);
    } else {
      setWeeklyCounts(setCount);
    }
  } catch (err) {
    console.error(isScreenings ? "❌ Error saving screening count:" : "❌ Error saving enrollment:", err);
  }

  setEditing(null);
  setEditValue("");
};


  // Add Patient (fixed)
  const handleSavePatient = async () => {
  // ✅ MRN validation
  if (!/^\d{9}$/.test(newPatient.mrn)) {
    alert("MRN must be exactly 9 digits.");
    return;
  }
  if (newPatient.diagnosis.length === 0) {
  alert("Please select at least one diagnosis.");
  return;
}
  // ✅ Normalize MRN
const enteredMRN = String(newPatient.mrn).trim();

// ✅ Check for duplicates across patients + duplicates
// Same MRN by the portal's one rule (leading zeros ignored: 12345 = 000012345).
const isDuplicate =
  patients.some((p) => sameMrnNumber(p.mrn, enteredMRN)) ||
  duplicates.some((p) => sameMrnNumber(p.mrn, enteredMRN));

if (isDuplicate) {
  alert("❌ Unable to add patient: MRN already exists in the system.");
  return;
}

// ✅ Second pass: the MRN is new, but is this the same PERSON already in the
// portal under a mistyped MRN? Matches on an exact DOB plus a fuzzy name
// (nickname, missing middle name/initial, typo). Advisory: it opens a dialog
// asking the user to recheck the MRN rather than refusing the add.
const matches = findPotentialDuplicates(
  { name: newPatient.name, dob: newPatient.dob, mrn: enteredMRN },
  [
    ...patients.map((p) => ({
      ...p,
      sourceLabel: p.source === "sheet" ? "Legacy Sheet" : "Referrals",
    })),
    ...duplicates.map((p) => ({ ...p, sourceLabel: "Duplicates" })),
  ]
);
if (matches.length) {
  setDupMatches(matches);
  return;
}

await performSavePatient();
};

// The actual write, split out of handleSavePatient so "Add Anyway" on the
// duplicate warning can reuse it without re-running the checks.
const performSavePatient = async () => {
  try {
      const consentsRef = collection(db, getConsentsPath());
      const docRef = await addDoc(consentsRef, {
        ...newPatient,
        status: "Lung Center Referrals",
        signedAt: serverTimestamp(),
      });

      // Add immediately to state
      setPatients((prev) => [
        {
          id: docRef.id,
          ...newPatient,
          status: "Lung Center Referrals",
          source: "firestore",
        },
        ...prev,
      ]);

      setDupMatches([]);
      setShowModal(false);
      setNewPatient({ name: "", dob: "", mrn: "", diagnosis: [] });
    } catch (err) {
      console.error("❌ Error adding patient:", err);
      alert("Failed to add patient.");
    }
  };

  // Preserve ID when moving to duplicates
  // Preserve ALL fields when moving to duplicates
const moveToDuplicates = async (patient) => {
  try {
    // Get the most recent snapshot from "consents"
    const consRef = doc(db, getConsentsPath(), patient.id);
    const consSnap = await getDoc(consRef);

    if (!consSnap.exists()) {
      alert("Patient not found in consents.");
      return;
    }

    // Extract all data
    const data = consSnap.data();

    // Deep copy all existing patient details
    const fullData = {
      ...data,
      name: data.name || patient.name || "",
      fullName: data.fullName || patient.fullName || "",
      dob: data.dob || patient.dob || "",
      callMade: data.callMade ?? null,
      scheduled: data.scheduled ?? null,
      dateScheduled: data.dateScheduled || "",
      status: "Potential Duplicate",
      study: data.study || "",
      notes: data.notes || "",
      diagnosis: normalizeDiagnosis(data.diagnosis || patient.diagnosis),
      consentUrl: data.consentUrl || data.fileUrl || "",
      fileUrl: data.fileUrl || "",
      movedAt: serverTimestamp(),
    };

    // Write to "duplicates" collection using the SAME doc ID
    const dupRef = doc(db, getDuplicatesPath(), patient.id);
    await setDoc(dupRef, fullData);

    // Delete original from "consents"
    await deleteDoc(consRef);

    // Update UI states
    setPatients((prev) => prev.filter((p) => p.id !== patient.id));
    setDuplicates((prev) => [
      ...prev,
      { id: patient.id, ...fullData, source: "duplicates" },
    ]);

    alert("✅ Patient moved to Potential Duplicates with all data preserved.");
  } catch (err) {
    console.error("❌ Error moving patient to duplicates:", err);
    alert("Failed to move patient.");
  }
};


  // Stats
  const totalEnrollments = coordinatorCounts.reduce(
    (sum, c) => sum + c.count,
    0
  );
 

  const totalScreenings = screeningCounts.reduce((sum, c) => sum + c.count, 0);
  const screeningPct = computeGoalPct(totalScreenings, screeningGoal); // null = goal not set
  // Days left in the month being VIEWED (a past month says it has ended).
  const monthDaysInfo = daysLeftInfo(monthId);

  const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;

  const today = new Date();
  const target = new Date(dateStr);

  const diff = Math.ceil(
    (target - today) / (1000 * 60 * 60 * 24)
  );

  return diff;
};

  // Past months for both circles, read when the History pop-up opens. Each
  // screening read is caught on its own so it can't hide enrollment history.
  const openGoalHistory = async () => {
    setShowGoalHistory(true);
    setGoalHistory({ loading: true, error: "", rows: [] });
    try {
      const current = currentPeriodKey(PERIOD_TYPES.MONTH);
      const monthIds = [...new Set([current, ...monthsWithData, ...screeningMonthsWithData])]
        .filter((id) => /^\d{4}-\d{2}$/.test(id) && id <= current)
        .sort()
        .reverse()
        .slice(0, 12);
      const readPeriod = async (path) =>
        (await getDocs(collection(db, path))).docs.map((d) => ({ name: d.id, ...d.data() }));
      const soft = (promise, fallback) => promise.catch(() => fallback);
      const docsByMonth = { enrollments: {}, screenings: {} };
      const goalsByMonth = { enrollments: {}, screenings: {} };
      await Promise.all(
        monthIds.map(async (id) => {
          const [eDocs, sDocs, eGoal, sGoal] = await Promise.all([
            readPeriod(getEnrollmentPeriodPath(id)),
            soft(readPeriod(getScreeningPeriodPath(id)), []),
            getDoc(doc(db, getMonthlyEnrollmentGoalsPath(), id)),
            soft(getDoc(doc(db, getMonthlyScreeningGoalsPath(), id)), null),
          ]);
          docsByMonth.enrollments[id] = eDocs;
          docsByMonth.screenings[id] = sDocs;
          goalsByMonth.enrollments[id] = goalFromDoc("enrollments", eGoal.exists(), eGoal.data());
          goalsByMonth.screenings[id] = sGoal
            ? goalFromDoc("screenings", sGoal.exists(), sGoal.data())
            : null;
        })
      );
      setGoalHistory({
        loading: false,
        error: "",
        rows: buildGoalHistory({
          monthIds,
          docsByMonth,
          goalsByMonth,
          closedByMonth: closedMonths,
          names: leaderboardCoordinators,
          currentMonthId: current,
        }),
      });
    } catch (err) {
      console.error("❌ Error loading goal history:", err);
      setGoalHistory({ loading: false, error: "Couldn't load past months. Please try again.", rows: [] });
    }
  };

  const handleLogout = async () => {
    await logoutAndClearSite();
    navigate("/"); // "/" redirects to /login; kept as-is, this is not a session change
  };

  const toggleExpand = (status) =>
    setExpanded((prev) => ({ ...prev, [status]: !prev[status] }));

  const toggleCoordinatorExpand = (name) =>
    setCoordinatorExpanded((prev) => ({ ...prev, [name]: !prev[name] }));

  const handleSearchChange = (status, value) =>
    setSearchQuery((prev) => ({ ...prev, [status]: value }));

  const filteredByStatus = (status) =>
    patients
      .filter((p) => p.status?.toLowerCase() === status.toLowerCase())
      .filter((p) =>
        `${p.mrn || ""} ${p.name || ""} ${p.fullName || ""} ${p.initials || ""}`
  .toLowerCase()
  .includes((searchQuery[status] || "").toLowerCase())
      );

  // KPI counts — derived from existing data only
  const enrolledCount = patients.filter(
    (p) => (p.status || "").toLowerCase() === "enrolled"
  ).length;
  // Diagnostics-enrolled patients sitting in other boxes — listed as a note
  // inside the Enrolled box. Doesn't change enrolledCount or any box.
  const diagnosticsElsewhere = patients.filter(
    (p) =>
      diagnosticsBadgeFor(p, studiesById).show &&
      (p.status || "").toLowerCase() !== "enrolled"
  );
  const pendingCount = patients.filter((p) => {
    const s = (p.status || "").toLowerCase();
    return s === "pending" || s === "unable to reach";
  }).length;
  const rescreenCount = patients.filter(
    (p) => (p.status || "").toLowerCase() === "rescreen"
  ).length;
  const goalPct = computeGoalPct(totalEnrollments, enrollmentGoal) ?? 0;

  const renderStatusBlock = (status) => {
    let patientsInStatus = filteredByStatus(status);

// 🔥 Sort ONLY for Rescreen
if (status === "Rescreen") {
  patientsInStatus = [...patientsInStatus].sort((a, b) => {
    const dateA = a.rescreenDate ? new Date(a.rescreenDate) : new Date(9999, 0);
    const dateB = b.rescreenDate ? new Date(b.rescreenDate) : new Date(9999, 0);
    return dateA - dateB;
  });
}
    const isExpanded = expanded[status];
    return (
      <div key={status} className="border-b border-white/5 last:border-none">
        {UI_REVAMP_ENABLED ? (
          <RevampBoxHeader
            title={statusLabel(status)}
            tone={boxToneFor(status)}
            count={patientsInStatus.length}
            open={isExpanded}
            onToggle={() => toggleExpand(status)}
          />
        ) : (
        <button
          type="button"
          onClick={() => toggleExpand(status)}
          className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition group ${
            isExpanded
              ? "bg-gradient-to-r from-purple-500/10 to-blue-500/10"
              : "hover:bg-white/[0.04]"
          }`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className={`w-1.5 h-1.5 rounded-full ${toneFor(status).dot}`} />
            <h4 className="text-sm font-semibold text-white truncate">
              {statusLabel(status)}
            </h4>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-white/[0.06] border border-white/10 text-slate-300">
              {patientsInStatus.length}
            </span>
          </div>
          <ChevronDown
            className={`w-3.5 h-3.5 text-slate-400 transition-transform shrink-0 ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>
        )}
        {isExpanded && (
          <div className="px-3 pb-3 pt-1">
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder={`Search in ${statusLabel(status)}`}
                className="w-full sm:w-80 pl-8 pr-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                value={searchQuery[status] || ""}
                onChange={(e) => handleSearchChange(status, e.target.value)}
              />
            </div>
            {status === "Enrolled" && diagnosticsElsewhere.length > 0 && (
              <div className="mb-2 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100">
                <div className="flex items-center gap-1.5 font-semibold">
                  <Stethoscope className="w-3.5 h-3.5 text-emerald-300" />
                  {diagnosticsElsewhere.length} patient
                  {diagnosticsElsewhere.length === 1 ? "" : "s"} enrolled in
                  diagnostics studies {diagnosticsElsewhere.length === 1 ? "is" : "are"} in
                  other boxes (not counted here)
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {diagnosticsElsewhere.map((p) => (
                    <button
                      key={p.source === "sheet" ? `s-${p.token}` : `f-${p.id}`}
                      type="button"
                      onClick={() => goToDetail(p)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.06] border border-white/10 hover:bg-emerald-500/20 text-[11px] text-slate-100 transition"
                      title={statusLabel(p.status) || ""}
                    >
                      {p.name || p.initials || "N/A"}
                      <span className="text-slate-400">· {statusLabel(p.status) || "—"}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {patientsInStatus.length === 0 ? (
              <p className="text-slate-500 text-xs italic px-1">No patients found.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">
                {patientsInStatus.map((patient, index) => (
                  <PatientCard
                    key={index}
                    index={index}
                    patient={patient}
                    status={status}
                    getDaysUntil={getDaysUntil}
                    onMoveDup={moveToDuplicates}
                    onOpen={goToDetail}
                    surveyAnswered={
                      PFT_SURVEY_TEXT_ENABLED && surveyAnsweredOnCard(patient, status, respondedSurveyKeys)
                    }
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  const renderFutureStudiesBlock = () => {
  const isExpanded = expanded["Future Studies"];

  return (
    <div className="border-b border-white/5">
      {UI_REVAMP_ENABLED ? (
        // No number: Future Studies holds one sub-box per diagnosis, so a count
        // here would be sub-boxes, not people, unlike every other box.
        <RevampBoxHeader
          title="Future Studies"
          tone={boxToneFor("Future Studies")}
          open={isExpanded}
          onToggle={() => toggleExpand("Future Studies")}
        />
      ) : (
      <button
        type="button"
        onClick={() => toggleExpand("Future Studies")}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition ${
          isExpanded
            ? "bg-gradient-to-r from-purple-500/10 to-blue-500/10"
            : "hover:bg-white/[0.04]"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-1.5 h-1.5 rounded-full ${toneFor("default").dot}`} />
          <h4 className="text-sm font-semibold text-white">Future Studies</h4>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-mono bg-white/[0.06] border border-white/10 text-slate-300">
            {futureStudyDiagnoses.length}
          </span>
        </div>
        <ChevronDown
          className={`w-3.5 h-3.5 text-slate-400 transition-transform shrink-0 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </button>
      )}

      {isExpanded && (
        <div className="px-2 pb-2 border-t border-white/5">
          {futureStudyDiagnoses.map((dx) =>
            renderStatusBlock(`Future Studies - ${dx}`)
          )}
        </div>
      )}
    </div>
  );
};

  const goToDetail = (patient) => {
    const pid = patient.source === "sheet" ? patient.token : patient.id;
    // The open section lives in different state per tab.
    const openSection =
      activeTab === "coordinators"
        ? Object.keys(coordinatorExpanded).find((k) => coordinatorExpanded[k])
        : Object.keys(expanded).find((k) => expanded[k]);
    navigate(`/coordinator/patient/${pid}?src=${patient.source}`, {
      state: {
        fromCoordinatorTab: activeTab,
        fromCoordinatorSection: openSection || null,
        fromCoordinatorPatientId: pid,
      },
    });
  };

  // Sidebar command groups — preserves every original navigation/handler
  const commandGroups = [
    {
      label: "Operations",
      icon: Workflow,
      actions: [
        // UI revamp: the personal home page, first in the sidebar (replaces the
        // header's Home button on this page). Admins and coordinators only:
        // reception also uses this page but has no home page.
        ...(UI_REVAMP_ENABLED && (isAdmin || roleForActiveSite === "coordinator")
          ? [
              {
                label: "Home",
                icon: HomeNavIcon,
                onClick: () => navigate("/home"),
                tone: "from-purple-500/30 to-violet-500/30 border-purple-300/30 text-purple-100",
              },
            ]
          : []),
        {
          label: "Add Patient",
          icon: UserPlus,
          onClick: () => setShowModal(true),
          tone: "from-emerald-500/30 to-teal-500/30 border-emerald-300/30 text-emerald-100",
        },
        {
          label: "Weekly Report",
          icon: FileText,
          onClick: goToSurvey,
          tone: "from-purple-500/30 to-fuchsia-500/30 border-purple-300/30 text-purple-100",
        },
        {
          label: "Research Calendar",
          icon: CalendarRange,
          onClick: goToResearchCalendar,
          tone: "from-violet-500/30 to-fuchsia-500/30 border-violet-300/30 text-violet-100",
        },
      ],
    },
    {
      label: "Patient Flow",
      icon: HeartPulse,
      actions: [
        {
          label: "Enrolled / Potential",
          icon: ClipboardList,
          onClick: goToEnrolledPatients,
          tone: "from-teal-500/30 to-cyan-500/30 border-teal-300/30 text-teal-100",
        },
        {
          label: "Studies",
          icon: Stethoscope,
          onClick: goToStudies,
          tone: "from-cyan-500/30 to-blue-500/30 border-cyan-300/30 text-cyan-100",
        },
        {
          label: "Contacts",
          icon: Phone,
          onClick: goToContacts,
          tone: "from-amber-500/30 to-orange-500/30 border-amber-300/30 text-amber-100",
        },
        {
          label: "Raw Database",
          icon: Database,
          onClick: goToRawDatabase,
          tone: "from-pink-500/30 to-rose-500/30 border-pink-300/30 text-pink-100",
        },
        // Patients found in uploaded PFT reports (admins and coordinators, like its route).
        ...(PFT_IMPORT_ENABLED && canSeePftUploads
          ? [{
              label: "Review PFT Patients",
              icon: Activity,
              onClick: goToPftUploads,
              tone: "from-emerald-500/30 to-teal-500/30 border-emerald-300/30 text-emerald-100",
            }]
          : []),
        // Possible duplicate patients, on screen only (admins and coordinators, like its route).
        ...(canScanDuplicates
          ? [{
              label: "Scan for Duplicates",
              icon: Users,
              onClick: openDuplicateScan,
              tone: "from-amber-500/30 to-rose-500/30 border-amber-300/30 text-amber-100",
            }]
          : []),
      ],
    },
    {
      label: "Navigation",
      icon: Layers,
      actions: [
        {
          label: "Statistics",
          icon: BarChart3,
          onClick: goToStatistics,
          tone: "from-blue-500/30 to-indigo-500/30 border-blue-300/30 text-blue-100",
        },
        {
          label: "VaultX",
          icon: BookOpen,
          onClick: goToVaultX,
          tone: "from-indigo-500/30 to-violet-500/30 border-indigo-300/30 text-indigo-100",
        },
        {
          label: "Query RegX",
          icon: MessageSquare,
          onClick: goToQuery,
          tone: "from-violet-500/30 to-purple-500/30 border-violet-300/30 text-violet-100",
        },
        {
          label: "SourceX",
          icon: FileText,
          onClick: goToSourceX,
          tone: "from-fuchsia-500/30 to-pink-500/30 border-fuchsia-300/30 text-fuchsia-100",
        },
      ],
    },
    {
      label: "System",
      icon: Activity,
      actions: [
        {
          label: "Log Out",
          icon: LogOut,
          onClick: handleLogout,
          tone: "from-red-500/30 to-rose-500/30 border-red-300/30 text-red-100",
        },
      ],
    },
  ];

  const leaderboardData = leaderboardMode === "monthly" ? coordinatorCounts : weeklyCounts;
  const screeningLeaderboardData =
    leaderboardMode === "monthly" ? screeningCounts : screeningWeeklyCounts;

  // Open the shared counts pop-up for one board, pre-filled with its numbers.
  const openLeaderboardEdit = (metric) => {
    if (!isAdmin) return; // leaderboard counts are admin-only (rules enforce it too)
    const monthlyRows = metric === "screenings" ? screeningCounts : coordinatorCounts;
    const weeklyRows = metric === "screenings" ? screeningWeeklyCounts : weeklyCounts;
    const draft = {};
    monthlyRows.forEach((c) => {
      draft[c.name] = { ...(draft[c.name] || {}), monthly: c.count.toString() };
    });
    weeklyRows.forEach((c) => {
      draft[c.name] = { ...(draft[c.name] || {}), weekly: c.count.toString() };
    });
    setLeaderboardEditMetric(metric);
    setLeaderboardDraft(draft);
    setShowLeaderboardEdit(true);
  };
  const openGoalEdit = (metric) => {
    const current = metric === "screenings" ? screeningGoal : enrollmentGoal;
    setGoalEditMetric(metric);
    setGoalDraft(current == null ? "" : String(current));
    setShowGoalEdit(true);
    // The rules in force for this month (carried on from an earlier month if
    // none were set for it).
    setRuleDraft([]);
    setRuleBaseline("[]");
    setRulesInheritedFrom(null);
    setRulesLoad({ loading: true, error: "" });
    loadRuleSets(metric)
      .then((sets) => {
        const inForce = rulesForMonth(sets, monthId);
        const rules = inForce ? inForce.rules : [];
        setRuleDraft(rules);
        setRuleBaseline(JSON.stringify(normalizeRules(rules)));
        setRulesInheritedFrom(inForce && inForce.effectiveFrom !== monthId ? inForce.effectiveFrom : null);
        setRulesLoad({ loading: false, error: "" });
      })
      .catch((err) => {
        console.error("❌ Could not load goal rules:", err);
        setRulesLoad({ loading: false, error: "Rules couldn't be loaded. The goal can still be saved." });
      });
  };
  // UI revamp: open Edit goal for the home page's pencil once the month's goals
  // are in (admins only, as the pencil here).
  useEffect(() => {
    if (!pendingGoalEdit || !isAdmin) return;
    const m = monthId;
    if ((pendingGoalEdit.monthId && pendingGoalEdit.monthId !== m) || goalLoadedFor.enrollments !== m || goalLoadedFor.screenings !== m) return;
    setPendingGoalEdit(null);
    openGoalEdit(pendingGoalEdit.metric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGoalEdit, monthId, goalLoadedFor, isAdmin]);
  const editingScreenings = leaderboardEditMetric === "screenings";
  const goalEditingScreenings = goalEditMetric === "screenings";
  const leaderboardEditPrefix = leaderboardMode === "monthly" ? "q" : "w";
  const leaderboardSaveType = leaderboardMode === "monthly" ? "quarterly" : "weekly";

  return (
    <StudiesByIdContext.Provider value={studiesById}>
    <div className="coordinator-page relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      {/* Mobile drawer backdrop */}
      {mobileDockOpen && (
        <div
          onClick={() => setMobileDockOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
        />
      )}

      <div className="flex min-h-screen">
        {/* ============ LEFT COMMAND DOCK SIDEBAR ============ */}
        <aside
          className={`fixed lg:sticky top-0 z-50 h-screen flex flex-col bg-slate-950/60 backdrop-blur-2xl border-r border-white/10 shadow-[8px_0_40px_-20px_rgba(124,58,237,0.45)] transition-all duration-300 ease-out ${
            isCommandDockOpen ? "w-64" : "w-[72px]"
          } ${
            mobileDockOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }`}
        >
          {/* Sidebar header */}
          <div
            className={`flex p-3 border-b border-white/10 ${
              isCommandDockOpen
                ? "items-center gap-2 justify-between"
                : "flex-col items-center justify-center gap-2"
            }`}
          >
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-blue-500 rounded-xl blur-md opacity-70" />
              {UI_REVAMP_ENABLED && isCommandDockOpen ? (
                // The logo says RegX, so it's shown full size with no text beside it.
                <img src="/RegX zoom.png" alt="RegX" className="relative h-12 w-auto max-w-[9rem] object-contain object-left" />
              ) : (
              <div className="relative w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500/30 via-indigo-500/30 to-blue-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center overflow-hidden p-1">
                <img
                  src="/RegX zoom.png"
                  alt="RegX"
                  className="w-full h-full object-contain scale-110"
                />
              </div>
              )}
            </div>
            {isCommandDockOpen && !UI_REVAMP_ENABLED && (
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold leading-tight">
                  Coordinator
                </div>
                <div className="text-sm font-semibold text-white leading-tight tracking-tight truncate">
                  Workflow Console
                </div>
              </div>
            )}
            <button
              onClick={() => setIsCommandDockOpen((o) => !o)}
              title={isCommandDockOpen ? "Collapse" : "Expand"}
              className="hidden lg:flex w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.1] hover:border-purple-300/30 items-center justify-center text-slate-300 hover:text-white transition shrink-0"
            >
              {isCommandDockOpen ? (
                <ChevronLeft className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
            </button>
          </div>

          {/* Sidebar groups */}
          <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 px-2 space-y-3 scrollbar-thin">
            {commandGroups.map((g) => {
              const GroupIcon = g.icon;
              return (
                <div key={g.label}>
                  {isCommandDockOpen ? (
                    <div className="px-1.5 mb-1.5 flex items-center gap-1.5">
                      {!UI_REVAMP_ENABLED && <GroupIcon className="w-3 h-3 text-purple-300" />}
                      <span className="text-[9px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
                        {g.label}
                      </span>
                      {!UI_REVAMP_ENABLED && (
                        <span className="ml-auto text-[9px] text-slate-500">
                          {g.actions.length}
                        </span>
                      )}
                    </div>
                  ) : UI_REVAMP_ENABLED ? (
                    // Collapsed: just space between sections, no icon.
                    <div className="h-2" aria-hidden="true" />
                  ) : (
                    <div className="flex flex-col items-center gap-1 mb-1.5">
                      <GroupIcon className="w-3 h-3 text-purple-300" />
                      <span className="block w-8 h-px bg-white/10" />
                    </div>
                  )}
                  <div className="space-y-0.5">
                    {g.actions.map((a) => {
                      const ActionIcon = a.icon;
                      return (
                        <button
                          key={a.label}
                          onClick={() => {
                            a.onClick();
                            setMobileDockOpen(false);
                          }}
                          title={a.label}
                          className={`group relative w-full flex items-center rounded-lg border border-transparent hover:border-white/10 hover:bg-white/[0.06] text-slate-200 hover:text-white transition-all ${
                            isCommandDockOpen
                              ? "gap-2.5 px-2 py-1.5 justify-start"
                              : "p-1.5 justify-center"
                          }`}
                        >
                          <span
                            className={`flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br ${a.tone} border shrink-0 transition group-hover:scale-105 group-hover:shadow-[0_0_15px_-2px_rgba(139,92,246,0.5)]`}
                          >
                            <ActionIcon className="w-3.5 h-3.5" />
                          </span>
                          {isCommandDockOpen && (
                            <span className="text-xs font-medium truncate">
                              {a.label}
                            </span>
                          )}
                          {!isCommandDockOpen && (
                            <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md bg-slate-900/95 border border-white/10 text-[11px] text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg">
                              {a.label}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {UI_REVAMP_ENABLED && <SidebarThemeToggle expanded={isCommandDockOpen} />}
          </nav>

          {/* Sidebar footer mini. UI revamp: not in the sidebar; the goal is in
              Goal Progress on the right. */}
          {!UI_REVAMP_ENABLED && (
          <div className={`p-2.5 border-t border-white/10 ${isCommandDockOpen ? "" : "flex justify-center"}`}>
            {isCommandDockOpen ? (
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/10">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center ring-1 ring-white/20 shrink-0">
                  <Target className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[9px] uppercase tracking-wider text-slate-400 leading-none">
                    Monthly Goal
                  </div>
                  <div className="text-xs font-semibold text-white truncate">
                    {totalEnrollments}/{enrollmentGoal} · {goalPct}%
                  </div>
                </div>
              </div>
            ) : (
              <div
                className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center ring-1 ring-white/20"
                title={`${totalEnrollments}/${enrollmentGoal} (${goalPct}%)`}
              >
                <Target className="w-3.5 h-3.5 text-white" />
              </div>
            )}
          </div>
          )}
        </aside>

        {/* ============ RIGHT CONTENT COLUMN ============ */}
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Sticky top bar */}
          <header className="sticky top-0 z-30 backdrop-blur-2xl bg-slate-950/40 border-b border-white/10 shadow-[0_8px_30px_-12px_rgba(124,58,237,0.35)]">
            <div className="px-3 sm:px-4 py-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => setMobileDockOpen(true)}
                  title="Open menu"
                  className="lg:hidden w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] flex items-center justify-center transition"
                >
                  <Menu className="w-4 h-4 text-slate-200" />
                </button>
                <div className="min-w-0">
                  {!UI_REVAMP_ENABLED && (
                    <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300/80 font-semibold">
                      Lung Innovations · RegX
                    </div>
                  )}
                  <h1 className="text-[15px] sm:text-base font-semibold tracking-tight leading-tight truncate">
                    {UI_REVAMP_ENABLED ? "Coordinator" : "Coordinator Portal"}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-2">
  <button
    onClick={() => window.open("https://lungscreening2025.web.app", "_blank", "noopener,noreferrer")}
    className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-sky-400 text-xs font-semibold text-white hover:bg-sky-300 active:scale-[0.98] transition-all"
  >
    LDCT Program
  </button>

  {isAdmin && (
    <button
      onClick={goToAdminDashboard}
      className="rx-header-admin-btn hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all"
    >
      <ShieldCheck className="w-3.5 h-3.5" />
      Back to Admin Dashboard
    </button>
  )}

  {/* UI revamp: Add Patient lives in the sidebar only. */}
  {!UI_REVAMP_ENABLED && (
  <button
    onClick={() => setShowModal(true)}
                  className="group relative inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Plus className="w-3.5 h-3.5 relative" />
                  <span className="relative hidden sm:inline">Add Patient</span>
                </button>
  )}
                <button
                  onClick={handleLogout}
                  title="Log out"
                  className="hidden sm:flex w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-red-500/15 hover:border-red-400/30 transition-all items-center justify-center group"
                >
                  <LogOut className="w-4 h-4 text-slate-200 group-hover:text-red-300 transition" />
                </button>
              </div>
            </div>
          </header>

          <main className="flex-1 px-3 sm:px-4 py-3 space-y-3">
            {/* KPI strip. UI revamp: the same numbers sit in the search row below
                (ToolbarStats) instead of tiles. */}
            {!UI_REVAMP_ENABLED && (
            <section className="coordinator-kpi-strip rx-kpi-strip grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {/* Patient-derived counts are suppressed during an outage for the
                  same reason the list is: a number computed from a partial set
                  reads as authoritative, so "12 enrolled" when the true figure
                  is 40 is more misleading than showing nothing. Duplicates below
                  come from Firestore and stay accurate. */}
              <KpiTile
                index={0}
                label="Total Patients"
                value={sheetUnavailable ? "—" : patients.length}
                icon={<Users className="w-3.5 h-3.5 text-white/80" />}
                accent="from-purple-500/20 to-indigo-500/20"
              />
              <KpiTile
                index={1}
                label="Enrolled"
                value={sheetUnavailable ? "—" : enrolledCount}
                icon={<ShieldCheck className="w-3.5 h-3.5 text-white/80" />}
                accent="from-emerald-500/20 to-teal-500/20"
                valueClass="text-emerald-300"
              />
              <KpiTile
                index={2}
                label="Pending"
                value={sheetUnavailable ? "—" : pendingCount}
                icon={<AlertTriangle className="w-3.5 h-3.5 text-white/80" />}
                accent="from-rose-500/20 to-pink-500/20"
                valueClass="text-rose-300"
              />
              <KpiTile
                index={3}
                label="Rescreens"
                value={sheetUnavailable ? "—" : rescreenCount}
                icon={<Clock className="w-3.5 h-3.5 text-white/80" />}
                accent="from-amber-500/20 to-orange-500/20"
                valueClass="text-amber-300"
              />
              <KpiTile
                index={4}
                label="Duplicates"
                value={duplicates.length}
                icon={<Layers className="w-3.5 h-3.5 text-white/80" />}
                accent="from-slate-500/20 to-slate-700/20"
                valueClass="text-slate-200"
              />
              <KpiTile
                index={5}
                label="Monthly Goal"
                value={goalPct}
                suffix="%"
                icon={<Target className="w-3.5 h-3.5 text-white/80" />}
                accent="from-blue-500/20 to-cyan-500/20"
                valueClass="text-blue-300"
              />
            </section>
            )}

            {/* Sticky toolbar: search + tabs */}
            <section className="sticky top-[56px] z-20 rounded-2xl border border-white/10 bg-slate-950/55 backdrop-blur-2xl px-2.5 py-2 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.5)]">
              <div
                className={
                  // UI revamp: numbers + tabs on the first row, search on its own
                  // full-width row underneath (order-last + basis-full).
                  UI_REVAMP_ENABLED
                    ? "flex flex-wrap items-center justify-between gap-2"
                    : "flex flex-col md:flex-row gap-2 items-stretch md:items-center"
                }
              >
                <div className={UI_REVAMP_ENABLED ? "relative basis-full order-last min-w-0" : "relative flex-1 min-w-0"}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search MRN / Name across all patients…"
                    className="w-full pl-9 pr-9 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                    value={masterSearch}
                    onChange={(e) => setMasterSearch(e.target.value)}
                  />
                  {masterSearch && (
                    <button
                      onClick={() => setMasterSearch("")}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* UI revamp: the patient numbers, in this row instead of tiles.
                    Patient-derived ones show "—" while the Sheet is down, as the
                    tiles did; Duplicates come from Firestore and stay accurate. */}
                {UI_REVAMP_ENABLED && (
                  <div role="group" aria-label="Patient numbers" className="rx-toolbar-stats flex flex-wrap items-center gap-y-1 min-w-0">
                    {[
                      ["Total Patients", sheetUnavailable ? "—" : patients.length, ""],
                      ["Enrolled", sheetUnavailable ? "—" : enrolledCount, "rx-stat--good"],
                      ["Pending", sheetUnavailable ? "—" : pendingCount, "rx-stat--alert"],
                      ["Rescreens", sheetUnavailable ? "—" : rescreenCount, "rx-stat--warn"],
                      ["Duplicates", duplicates.length, ""],
                    ].map(([label, value, tone]) => (
                      <div key={label} className="rx-toolbar-stat px-3">
                        <div className="rx-toolbar-stat-label text-[9px] uppercase tracking-[0.14em] font-medium whitespace-nowrap">{label}</div>
                        <div className={`rx-toolbar-stat-value text-sm font-semibold tabular-nums leading-tight ${tone}`}>{value}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="inline-flex p-1 rounded-xl bg-white/[0.04] border border-white/10 gap-0.5 shrink-0">
                  {[
                    ["statuses", "Statuses"],
                    ["coordinators", "Coordinators"],
                    ["duplicates", "Duplicates"],
                  ].map(([k, label]) => (
                    <button
                      key={k}
                      onClick={() => setActiveTab(k)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition ${
                        activeTab === k
                          ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-[0_4px_12px_-2px_rgba(139,92,246,0.5)]"
                          : "text-slate-300 hover:text-white hover:bg-white/[0.05]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Main grid: workflow area + utility rail */}
            <section className="grid grid-cols-1 xl:grid-cols-12 gap-3">
              {/* Workflow area */}
              <div className="xl:col-span-9 min-w-0">
                {loading ? (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-10 text-center">
                    <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] text-slate-400">
                      <Activity className="w-4 h-4 animate-pulse text-purple-300" />
                      Loading…
                    </div>
                  </div>
                ) : sheetUnavailable ? (
                  // Deliberately replaces the list ENTIRELY, even when Firestore
                  // records loaded fine. A partial list is indistinguishable from
                  // a complete one, and acting on "this patient isn't here" when
                  // they simply failed to load is the worst outcome available. No
                  // data beats misleading data.
                  <div className="rounded-2xl border border-amber-400/30 bg-amber-500/[0.07] backdrop-blur-xl p-10 text-center">
                    <div className="mx-auto w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-400/30 flex items-center justify-center mb-3">
                      <AlertTriangle className="w-5 h-5 text-amber-300" />
                    </div>
                    <h3 className="text-sm font-semibold text-white">
                      Patient records are temporarily unavailable.
                    </h3>
                    <p className="text-xs text-amber-200/80 mt-2 max-w-md mx-auto">
                      We couldn't reach the patient record store after several attempts. The list
                      is hidden rather than shown incomplete, so nothing here is missing without
                      you knowing. This is usually brief — try again in a minute.
                    </p>
                    <button
                      onClick={() => window.location.reload()}
                      className="mt-4 px-4 py-1.5 rounded-lg text-xs font-semibold bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/30 text-amber-100 transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                ) : patients.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] backdrop-blur-xl p-10 text-center">
                    <div className="mx-auto w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 border border-white/10 flex items-center justify-center mb-3">
                      <Users className="w-5 h-5 text-purple-300" />
                    </div>
                    <h3 className="text-sm font-semibold text-white">No patients to show yet.</h3>
                  </div>
                ) : masterSearch.trim() ? (
                  <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3">
                    <div className="flex items-center gap-2 mb-3">
                      <Search className="w-3.5 h-3.5 text-purple-300" />
                      <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
                        Master Search Results
                      </h2>
                      <span className="ml-auto text-[10px] text-slate-400">
                        Query: <span className="text-white font-medium">{masterSearch}</span>
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">
                      {patients
                        .filter((p) => {
                          const s = masterSearch.toLowerCase();
                          return (
                            (p.mrn && p.mrn.toLowerCase().includes(s)) ||
                            (p.name && p.name.toLowerCase().includes(s)) ||
                            (p.fullName && p.fullName.toLowerCase().includes(s)) ||
                            (p.initials && p.initials.toLowerCase().includes(s))
                          );
                        })
                        .map((patient, index) => (
                          <PatientCard
                            key={index}
                            index={index}
                            patient={patient}
                            status={patient.status}
                            getDaysUntil={getDaysUntil}
                            onOpen={goToDetail}
                          />
                        ))}
                    </div>
                  </div>
                ) : activeTab === "statuses" ? (
                  <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70 pointer-events-none" />
                    <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/5">
                      {!UI_REVAMP_ENABLED && <Workflow className="w-3.5 h-3.5 text-purple-300" />}
                      <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
                        Patient Statuses
                      </h2>
                      {!UI_REVAMP_ENABLED && (
                        <span className="ml-auto text-[10px] text-slate-400">
                          {commonStatuses.length} categories
                        </span>
                      )}
                    </div>
                    {UI_REVAMP_ENABLED &&
                      revampBoxOrder(commonStatuses).map((status) =>
                        status === FUTURE_STUDIES_SLOT ? (
                          <Fragment key={status}>{renderFutureStudiesBlock()}</Fragment>
                        ) : (
                          renderStatusBlock(status)
                        )
                      )}
                    {!UI_REVAMP_ENABLED && commonStatuses.map((status) => {
                      // Render the Future Studies block immediately after the
                      // Deceased block, so the board order is:
                      // … Rescreen → Deceased → Future Studies → …
                      if (status === "Deceased") {
                        return (
                          <Fragment key={status}>
                            {renderStatusBlock(status)}
                            {renderFutureStudiesBlock()}
                          </Fragment>
                        );
                      }
                      return renderStatusBlock(status);
                    })}
                  </div>
                ) : activeTab === "duplicates" ? (
                  <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3">
                    <div className="flex items-center gap-2 mb-3">
                      {!UI_REVAMP_ENABLED && <Layers className="w-3.5 h-3.5 text-rose-300" />}
                      <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
                        Potential Duplicates
                      </h2>
                      <span className="ml-auto text-[10px] text-slate-400">
                        {duplicates.length} total
                      </span>
                    </div>
                    <p className="mb-3 text-[10px] leading-relaxed text-amber-200/80 italic">
                      Records with no activity for 30 days are automatically
                      archived and removed from this list.
                      {duplicates.some((p) => p.source === "sheet") &&
                        " Google Sheet patients stay here until their status is changed on the chart."}
                    </p>
                    {duplicates.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-center">
                        <p className="text-xs text-slate-400 italic">No potential duplicates found.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-3">
                        {duplicates.map((patient, index) => (
                          <PatientCard
                            key={index}
                            index={index}
                            patient={patient}
                            status="Potential Duplicate"
                            getDaysUntil={getDaysUntil}
                            onOpen={goToDetail}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70 pointer-events-none" />
                    <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/5">
                      {!UI_REVAMP_ENABLED && <Users className="w-3.5 h-3.5 text-purple-300" />}
                      <h2 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
                        Coordinators
                      </h2>
                      <span className="ml-auto text-[10px] text-slate-400">
                        {coordinators.length} total
                      </span>
                    </div>
                    {coordinators.map((coord) => {
                      const isOpen = coordinatorExpanded[coord];
                      return (
                        <div
                          key={coord}
                          className="border-b border-white/5 last:border-none"
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setCoordinatorExpanded((prev) => ({
                                ...prev,
                                [coord]: !prev[coord],
                              }))
                            }
                            className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition ${
                              isOpen
                                ? "bg-gradient-to-r from-purple-500/10 to-blue-500/10"
                                : "hover:bg-white/[0.04]"
                            }`}
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[10px] font-bold text-white ring-1 ring-white/20 shrink-0">
                                {coord.charAt(0).toUpperCase()}
                              </div>
                              <h4 className="text-sm font-semibold text-white truncate">
                                {coord}
                              </h4>
                            </div>
                            <ChevronDown
                              className={`w-3.5 h-3.5 text-slate-400 transition-transform shrink-0 ${
                                isOpen ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                          {isOpen && (
                            <div className="px-2 pb-2 border-t border-white/5">
                              {coordinatorStatusTypes.map((type) =>
                                renderStatusBlock(`${type} (${coord})`)
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Utility rail: goal + leaderboard */}
              <aside className="xl:col-span-3 min-w-0 space-y-3">
                {/* Goal widget */}
                <div className="coordinator-utility-panel relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3 overflow-hidden">
                  <div className="coordinator-utility-glow absolute inset-0 bg-gradient-to-br from-emerald-500/[0.06] via-transparent to-teal-500/[0.06] pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      {!UI_REVAMP_ENABLED && <Target className="w-3.5 h-3.5 text-emerald-300" />}
                      <h3 className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white">
                        Goal Progress
                      </h3>
                      {UI_REVAMP_ENABLED ? (
                        <button
                          type="button"
                          onClick={openGoalHistory}
                          className="rx-icon-button ml-auto"
                          title="Past months for both goals"
                          aria-label="History"
                        >
                          <History className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={openGoalHistory}
                          className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[10px] font-semibold text-slate-300 transition"
                          title="Past months for both goals"
                        >
                          <History className="w-2.5 h-2.5" />
                          History
                        </button>
                      )}
                    </div>
                    {/* Which month the circles show (the leaderboard's month
                        picker below), and how much of it is left. */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      <span className="text-[10px] font-semibold text-slate-300">
                        {periodLabel(PERIOD_TYPES.MONTH, monthId) || monthId}
                      </span>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-400/10 border border-amber-400/20 text-[9px] text-amber-200">
                        {!UI_REVAMP_ENABLED && <Clock className="w-2.5 h-2.5" />}
                        {monthDaysInfo.kind === "past"
                          ? "Month ended"
                          : monthDaysInfo.kind === "future"
                          ? "Not started"
                          : `${monthDaysInfo.days} day${monthDaysInfo.days !== 1 ? "s" : ""} left`}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <GoalCircle
                        metric="enrollments"
                        label="Randomizations"
                        total={totalEnrollments}
                        goal={enrollmentGoal}
                        pct={goalPct}
                        canEditGoal={isAdmin}
                        onEditGoal={() => openGoalEdit("enrollments")}
                      />
                      <GoalCircle
                        metric="screenings"
                        label="Screenings"
                        total={totalScreenings}
                        goal={screeningGoal}
                        pct={screeningPct}
                        canEditGoal={isAdmin}
                        onEditGoal={() => openGoalEdit("screenings")}
                      />
                    </div>
                  </div>
                </div>

                {/* Leaderboard widget */}
                <div className="coordinator-utility-panel relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-3 overflow-hidden">
                  <div className="coordinator-utility-glow absolute inset-0 bg-gradient-to-br from-amber-500/[0.06] via-transparent to-orange-500/[0.06] pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-2 min-w-0">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {!UI_REVAMP_ENABLED && <Trophy className="w-3.5 h-3.5 text-amber-300 shrink-0" />}
                        <h3 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white truncate">
                          Leaderboards
                        </h3>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {isAdmin && (
                          <button
                            onClick={closeMonth}
                            disabled={savingAndResetting}
                            className="inline-flex h-7 items-center gap-1 whitespace-nowrap rounded-lg border border-emerald-400/30 bg-emerald-500/15 px-2 text-[10px] font-semibold text-emerald-200 transition hover:bg-emerald-500/25 disabled:cursor-not-allowed disabled:opacity-50"
                            title="Record a signed snapshot of this month's enrollments and screenings. Counts are not reset."
                          >
                            {!UI_REVAMP_ENABLED && <Sparkles className="w-2.5 h-2.5 shrink-0" />}
                            {savingAndResetting
                              ? "Closing"
                              : closedMonths[monthId]
                              ? "Re-close"
                              : "Close"}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                    <div className="inline-flex p-1 rounded-xl bg-white/[0.04] border border-white/10 gap-0.5">
                      {[
                        ["monthly", "Monthly"],
                        ["weekly", "Weekly"],
                      ].map(([k, label]) => (
                        <button
                          key={k}
                          onClick={() => setLeaderboardMode(k)}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition ${
                            leaderboardMode === k
                              ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-[0_4px_12px_-2px_rgba(251,191,36,0.5)]"
                              : "text-slate-300 hover:text-white"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                      {/* Which period the board is showing. Beside the mode
                          toggle because the two answer the same question —
                          which numbers am I looking at — and because the header
                          row above is far too narrow to hold a date control.
                          Only the selector for the ACTIVE mode is shown; the
                          board only ever displays one of them. */}
                      {leaderboardMode === "monthly" ? (
                        <select
                          value={monthId}
                          onChange={(e) => setMonthId(e.target.value)}
                          title="Which month these counts belong to"
                          className="h-7 max-w-[9.5rem] rounded-xl border border-white/10 bg-white/[0.04] px-2 text-[10px] font-semibold text-slate-200 outline-none transition hover:border-amber-300/30"
                        >
                          {monthOptions.map((k) => (
                            <option key={k} value={k} className="bg-[#0b0820]">
                              {periodLabel(PERIOD_TYPES.MONTH, k) || k}
                              {closedMonths[k] ? " ✓" : ""}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <select
                          value={weekId}
                          onChange={(e) => setWeekId(e.target.value)}
                          title="Which week these counts belong to"
                          className="h-7 max-w-[9.5rem] rounded-xl border border-white/10 bg-white/[0.04] px-2 text-[10px] font-semibold text-slate-200 outline-none transition hover:border-amber-300/30"
                        >
                          {weekOptions.map((k) => (
                            <option key={k} value={k} className="bg-[#0b0820]">
                              {periodLabel(PERIOD_TYPES.WEEK, k) || k}
                            </option>
                          ))}
                        </select>
                      )}
                      {/* Editing a past period is allowed — that is the point —
                          but it should never happen without the person noticing. */}
                      {(leaderboardMode === "monthly" ? viewingPastMonth : viewingPastWeek) && (
                        <span
                          title={
                            closedMonths[monthId]
                              ? `Closed by ${closedMonths[monthId].closedBy || "someone"}. Edits are still allowed and are recorded.`
                              : "You are editing a past period. Changes are recorded with your name."
                          }
                          className="inline-flex h-7 items-center rounded-xl border border-amber-400/40 bg-amber-400/15 px-2 text-[10px] font-semibold text-amber-100"
                        >
                          past{leaderboardMode === "monthly" && closedMonths[monthId] ? " · closed" : ""}
                        </span>
                      )}
                    </div>
                    {/* Two boards, same period controls above. Each has its
                        own Edit, exactly like the single board before. */}
                    <div className="space-y-3">
                      <LeaderboardList
                        metric="enrollments"
                        title="Randomizations"
                        ranked={rankLeaderboard(leaderboardData)}
                        emptyText="No randomizations yet."
                        onEdit={isAdmin ? () => openLeaderboardEdit("enrollments") : undefined}
                      />
                      <div className="border-t border-white/10" />
                      <LeaderboardList
                        metric="screenings"
                        title="Screenings"
                        ranked={rankLeaderboard(screeningLeaderboardData)}
                        emptyText="No screenings yet."
                        onEdit={isAdmin ? () => openLeaderboardEdit("screenings") : undefined}
                      />
                    </div>
                  </div>
                </div>
              </aside>
            </section>

            <div className="h-6" />
          </main>
        </div>
      </div>
      {/* From the 1st: "Close <last month>?" while it's unclosed (admins) */}
      <MonthClosePrompt
        onClosed={(m, totals) =>
          setClosedMonths((prev) => ({ ...prev, [m]: { closedBy: auth.currentUser?.email || "", total: totals.total } }))
        }
        onReview={() => navigate("/admin/weekly-reports")}
      />
      {/* Past months for both goal circles */}
      <GoalHistoryModal
        open={showGoalHistory}
        loading={goalHistory.loading}
        error={goalHistory.error}
        rows={goalHistory.rows}
        selectedMonthId={monthId}
        onPick={(id) => {
          setMonthId(id);
          setLeaderboardMode("monthly");
          setShowGoalHistory(false);
        }}
        onClose={() => setShowGoalHistory(false)}
      />
      {/* Edit Goal Modal (enrollments or screenings) */}
<AnimatePresence>
  {showGoalEdit && (
    <motion.div
      {...modalBackdrop}
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]"
      onMouseDown={(e) => {
        // Not while rules have unsaved edits, so a stray click can't lose them.
        if (e.target === e.currentTarget && !goalSaving && !rulesDirty) setShowGoalEdit(false);
      }}
    >
      <motion.div
        {...modalPanel}
        className="goal-edit-modal relative w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(16,185,129,0.6)] text-slate-100"
      >
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 opacity-80 pointer-events-none" />

        <div className="relative px-5 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/30 via-teal-500/30 to-cyan-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
            <Target className="w-5 h-5 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80 font-semibold">
              Monthly Goal
            </div>
            <h2 className="text-base font-semibold text-white">
              {goalEditingScreenings ? "Edit Screening Goal" : "Edit Randomization Goal"}
            </h2>
            <div className="text-[11px] text-slate-400">
              {periodLabel(PERIOD_TYPES.MONTH, monthId) || monthId}
            </div>
          </div>

          <button
            onClick={() => setShowGoalEdit(false)}
            disabled={goalSaving}
            className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 flex items-center justify-center text-slate-300 hover:text-white transition disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative p-5 overflow-y-auto flex-1">
          <label className="block max-w-xs">
            <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
              {goalEditingScreenings ? "Screening Goal for This Month" : "Randomization Goal for This Month"}
            </span>
            <input
              type="number"
              min={0}
              value={goalDraft}
              onChange={(e) => setGoalDraft(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-emerald-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-emerald-500/20 transition"
              placeholder={goalEditingScreenings ? "Example: 30" : "Example: 12"}
            />
          </label>
          {goalEditingScreenings && (
            <p className="mt-2 text-[11px] text-slate-400">
              Leave it at 0 for no goal; the circle then shows "Goal not set".
            </p>
          )}
          <GoalRulesEditor
            rules={ruleDraft}
            onChange={setRuleDraft}
            monthId={monthId}
            inheritedFrom={rulesInheritedFrom}
            studiesById={studiesById}
            loading={rulesLoad.loading}
            error={rulesLoad.error}
          />
        </div>

        <div className="relative px-5 py-3 border-t border-white/10 bg-slate-950/50 flex items-center justify-end gap-2">
          <button
            onClick={() => setShowGoalEdit(false)}
            disabled={goalSaving}
            className="px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm font-medium text-slate-200 hover:text-white transition disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={saveGoal}
            disabled={goalSaving || rulesLoad.loading}
            className="goal-edit-save inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <CheckIcon className="w-4 h-4" />
            {goalSaving ? "Saving..." : rulesDirty ? "Save goal & rules" : "Save Goal"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>
      {/* Edit Leaderboard Modal */}
      <AnimatePresence>
      {showLeaderboardEdit && (
        <motion.div
          {...modalBackdrop}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !leaderboardSaving) setShowLeaderboardEdit(false);
          }}
        >
          <motion.div {...modalPanel} className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col">
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400 opacity-80 pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-amber-500/15 blur-[120px]" />
              <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-purple-600/15 blur-[120px]" />
            </div>

            {/* Header */}
            <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
              <div className="relative shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl blur-md opacity-70" />
                <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/30 via-orange-500/30 to-rose-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-white" strokeWidth={2.2} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80 font-semibold leading-tight">
                  Leaderboard
                </div>
                <h2 className="text-base font-semibold tracking-tight text-white leading-tight">
                  {editingScreenings ? "Edit Screenings" : "Edit Randomizations"}
                </h2>
              </div>
              <button
                onClick={() => setShowLeaderboardEdit(false)}
                disabled={leaderboardSaving}
                className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0 disabled:opacity-50"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="relative overflow-y-auto flex-1 p-5">
              <p className="mb-3 px-2 text-[11px] text-slate-400">
                Goal points approved on Admin Reports are added to these counts automatically, so don't re-enter them.
              </p>
              <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center mb-2 px-2">
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">
                  Coordinator
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-300 text-center w-20">
                  Monthly
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-blue-300 text-center w-20">
                  Weekly
                </span>
              </div>

              <ul className="space-y-1">
                {leaderboardCoordinators.map((coord) => {
                  const draft = leaderboardDraft[coord] || {};
                  const monthly = draft.monthly ?? "0";
                  const weekly = draft.weekly ?? "0";
                  return (
                    <li
                      key={coord}
                      className="grid grid-cols-[1fr_auto_auto] gap-2 items-center px-2 py-2 rounded-lg border border-white/5 bg-white/[0.025] hover:bg-white/[0.04] transition"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-500 flex items-center justify-center text-[10px] font-bold text-white ring-1 ring-white/20 shrink-0">
                          {coord.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-white truncate">
                          {coord}
                        </span>
                      </div>
                      <input
                        type="number"
                        min={0}
                        value={monthly}
                        onChange={(e) =>
                          setLeaderboardDraft((prev) => ({
                            ...prev,
                            [coord]: { ...(prev[coord] || {}), monthly: e.target.value },
                          }))
                        }
                        className="w-20 px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-center text-sm text-slate-100 font-mono focus:outline-none focus:border-amber-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-amber-500/20 transition"
                      />
                      <input
                        type="number"
                        min={0}
                        value={weekly}
                        onChange={(e) =>
                          setLeaderboardDraft((prev) => ({
                            ...prev,
                            [coord]: { ...(prev[coord] || {}), weekly: e.target.value },
                          }))
                        }
                        className="w-20 px-2 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 text-center text-sm text-slate-100 font-mono focus:outline-none focus:border-blue-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-blue-500/20 transition"
                      />
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Footer */}
            <div className="relative px-5 py-3 border-t border-white/10 bg-slate-950/50 backdrop-blur-xl flex items-center justify-end gap-2 shrink-0">
              <div className="hidden sm:flex items-center gap-1.5 mr-auto text-[10px] uppercase tracking-wider text-slate-400">
                <Sparkles className="w-3 h-3 text-amber-300" />
                {editingScreenings
                  ? "Writes to coordinatorScreenings / coordinatorScreeningsWeekly"
                  : "Writes to coordinatorEnrollments / coordinatorWeekly"}
              </div>
              <button
                onClick={() => setShowLeaderboardEdit(false)}
                disabled={leaderboardSaving}
                className="px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm font-medium text-slate-200 hover:text-white transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setLeaderboardSaving(true);
                  try {
                    // Diff against current values and only write changed cells,
                    // on whichever board (enrollments / screenings) was opened.
                    const metric = leaderboardEditMetric;
                    const monthlyRows = metric === "screenings" ? screeningCounts : coordinatorCounts;
                    const weeklyRows = metric === "screenings" ? screeningWeeklyCounts : weeklyCounts;
                    for (const coord of leaderboardCoordinators) {
                      const draft = leaderboardDraft[coord] || {};
                      const currentMonthly =
                        monthlyRows.find((c) => c.name === coord)?.count ?? 0;
                      const currentWeekly =
                        weeklyRows.find((c) => c.name === coord)?.count ?? 0;
                      const nextMonthly = Math.max(parseInt(draft.monthly) || 0, 0);
                      const nextWeekly = Math.max(parseInt(draft.weekly) || 0, 0);
                      if (nextMonthly !== currentMonthly) {
                        await saveEdit(coord, "quarterly", draft.monthly, metric);
                      }
                      if (nextWeekly !== currentWeekly) {
                        await saveEdit(coord, "weekly", draft.weekly, metric);
                      }
                    }
                  } finally {
                    setLeaderboardSaving(false);
                    setShowLeaderboardEdit(false);
                  }
                }}
                disabled={leaderboardSaving}
                className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(251,191,36,0.5)] active:scale-[0.98] transition-all overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <CheckIcon className="w-4 h-4 relative" />
                <span className="relative">{leaderboardSaving ? "Saving…" : "Save Changes"}</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Add Patient Modal */}
      <AnimatePresence>
      {showModal && (
        <motion.div
          {...modalBackdrop}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <motion.div {...modalPanel} className="relative w-full max-w-md max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col">
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 opacity-80 pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-emerald-500/15 blur-[120px]" />
              <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-blue-600/15 blur-[120px]" />
            </div>

            {/* Header */}
            <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
              <div className="relative shrink-0">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl blur-md opacity-70" />
                <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/30 via-teal-500/30 to-cyan-500/30 border border-white/15 ring-1 ring-white/20 flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-white" strokeWidth={2.4} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80 font-semibold leading-tight">
                  Operations
                </div>
                <h2 className="text-base font-semibold tracking-tight text-white leading-tight">
                  Add Patient
                </h2>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 hover:border-white/20 flex items-center justify-center text-slate-300 hover:text-white transition shrink-0"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="relative overflow-y-auto flex-1 p-5 space-y-3">
              <label className="block">
                <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                  Full Name
                </span>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={newPatient.name}
                    onChange={(e) =>
                      setNewPatient({ ...newPatient, name: e.target.value })
                    }
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition"
                  />
                </div>
              </label>

              <label className="block">
                <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                  Date of Birth
                </span>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    type="date"
                    value={newPatient.dob}
                    onChange={(e) =>
                      setNewPatient({ ...newPatient, dob: e.target.value })
                    }
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark]"
                  />
                </div>
              </label>

              <label className="block">
                <span className="block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1">
                  MRN (9 digits)
                </span>
                <div className="relative">
                  <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="9-digit MRN"
                    maxLength={9}
                    inputMode="numeric"
                    pattern="\d*"
                    value={newPatient.mrn}
                    onChange={(e) =>
                      setNewPatient({ ...newPatient, mrn: e.target.value })
                    }
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition font-mono tracking-wider"
                  />
                </div>
              </label>

              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <HeartPulse className="w-3 h-3 text-purple-300" />
                  <span className="text-[10px] uppercase tracking-wider font-medium text-slate-400">
                    Select Diagnosis
                  </span>
                  {newPatient.diagnosis.length > 0 && (
                    <span className="ml-1 text-[10px] text-purple-300 font-semibold">
                      {newPatient.diagnosis.length} selected
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {DIAGNOSES.map((d) => {
                    const active = newPatient.diagnosis.includes(d);
                    return (
                      <button
                        type="button"
                        key={d}
                        onClick={() => {
                          if (active) {
                            setNewPatient({
                              ...newPatient,
                              diagnosis: newPatient.diagnosis.filter((x) => x !== d),
                            });
                          } else {
                            setNewPatient({
                              ...newPatient,
                              diagnosis: [...newPatient.diagnosis, d],
                            });
                          }
                        }}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                          active
                            ? "bg-gradient-to-r from-purple-500/30 to-blue-500/30 border-purple-300/40 text-white shadow-[0_0_15px_-2px_rgba(139,92,246,0.5)]"
                            : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08] hover:border-purple-300/30 hover:text-white"
                        }`}
                      >
                        {active ? (
                          <CheckIcon className="w-3 h-3 text-emerald-300" />
                        ) : (
                          <span className="w-3 h-3 rounded-full border border-white/20" />
                        )}
                        {diagnosisLabel(d)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="relative px-5 py-3 border-t border-white/10 bg-slate-950/50 backdrop-blur-xl flex items-center justify-end gap-2 shrink-0">
              <div className="hidden sm:flex items-center gap-1.5 mr-auto text-[10px] uppercase tracking-wider text-slate-400">
                <Sparkles className="w-3 h-3 text-emerald-300" />
                New patient
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm font-medium text-slate-200 hover:text-white transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePatient}
                className="group relative inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(16,185,129,0.6)] active:scale-[0.98] transition-all overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <UserPlus className="w-4 h-4 relative" />
                <span className="relative">Save</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Name + DOB duplicate warning, layered above the Add Patient modal so
          the typed values stay on screen behind it. */}
      <PotentialDuplicateDialog
        matches={dupMatches}
        entered={{
          name: newPatient.name,
          dob: newPatient.dob,
          mrn: newPatient.mrn,
        }}
        onCancel={() => setDupMatches([])}
        onProceed={() => {
          if (
            window.confirm(
              "Add this patient anyway? A possible duplicate was found with the same date of birth."
            )
          ) {
            performSavePatient();
          }
        }}
      />
    </div>
    </StudiesByIdContext.Provider>
  );
};

export default CoordinatorPage;
